import React, { useState, useEffect, useCallback, useDeferredValue, useTransition, useMemo, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Spin,
  Tooltip,
  message,
  Divider,
  Input,
  Select,
  Descriptions,
  Collapse,
  theme,
  Modal,
  Alert,
  List,
  Switch,
  Segmented,
  DatePicker,
  Radio,
  Dropdown,
  Checkbox
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SendOutlined,
  CopyOutlined,
  MailOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
  TrophyOutlined,
  QuestionCircleOutlined,
  EyeOutlined,
  CaretRightOutlined,
  ApartmentOutlined,
  EditOutlined,
  CalendarOutlined,
  SaveOutlined,
  CloseCircleFilled,
  MobileOutlined,
  LaptopOutlined,
  HistoryOutlined,
  MoreOutlined,
  LinkOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useParams, useLocation } from 'react-router-dom';
import { Pie, Column } from '@ant-design/plots';
import dayjs from 'dayjs';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import campaignService from '../services/campaignService';
import smsService from '../services/smsService';
import whatsappService from '../services/whatsappService';
import { resolveDepartmentGroup } from '../constants/departmentGroups';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const TARGET_LIMIT = 50;
const TARGET_FIELD_SELECTION = [
  '_id',
  'status',
  'score',
  'startedAt',
  'completedAt',
  'participantId.name',
  'participantId.surname',
  'participantId.email',
  'participantId.department',
  'participantId.businessUnit.name',
  'participantId.phoneNumber.full',
  'participantId.phoneNumber.countryCode',
  'participantId.phoneNumber.number',
  'smsInvitation',
  'whatsappInvitation',
  'invitations.sms',
  'invitations.whatsapp',
  'sessionData',
  'attempts'
].join(',');

const CAMPAIGN_SUMMARY_FIELDS = [
  'name',
  'status',
  'startDate',
  'endDate',
  'createdBy.name',
  'createdBy.email',
  'questionnaire.title'
].join(',');

const SMS_STAGE_COLORS = {
  Sent: '#2E86AB',
  Delivered: '#52c41a',
  Pending: '#faad14',
  Failed: '#ff4d4f'
};

const WHATSAPP_STAGE_COLORS = {
  Sent: '#2E86AB',
  Delivered: '#52c41a',
  Pending: '#faad14',
  Failed: '#ff4d4f'
};

// Helpers for participant display and phone resolution
const getParticipantName = (participant) => {
  if (!participant) return 'Participant';
  const fullName = `${participant.name || ''} ${participant.surname || ''}`.trim();
  if (fullName) return fullName;
  if (participant.email) return participant.email;
  return 'Participant';
};

const getParticipantPhone = (participant) => {
  if (!participant) return '';
  if (participant.phoneNumber?.full) {
    return participant.phoneNumber.full;
  }
  if (participant.phoneNumber?.countryCode && participant.phoneNumber?.number) {
    return `${participant.phoneNumber.countryCode}${participant.phoneNumber.number}`;
  }
  return '';
};

const ALL_EXPORT_FIELDS = [
  { key: 'name',             label: 'Name',              default: true },
  { key: 'surname',          label: 'Surname',           default: true },
  { key: 'email',            label: 'Email',             default: true },
  { key: 'phone',            label: 'Phone Number',      default: true },
  { key: 'businessUnit',     label: 'Business Unit',     default: true },
  { key: 'department',       label: 'Department',        default: true },
  { key: 'status',           label: 'Status',            default: true },
  { key: 'score',            label: 'Score',             default: true },
  { key: 'startedAt',        label: 'Start Date',        default: true },
  { key: 'completedAt',      label: 'Finish Date',       default: true },
  { key: 'deliveryChannel',  label: 'Delivery Channel',  default: true },
  { key: 'completionTime',   label: 'Completion Time',   default: false },
  { key: 'device',           label: 'Device',            default: false },
];

// Normalize campaign target record for table usage
const simplifyTargetRecord = (target = {}) => {
  const participant = target.participantId || {};
  const phone = participant.phoneNumber || {};
  const businessUnit = participant.businessUnit || {};

  return {
    _id: target._id,
    status: target.status || 'pending',
    score: typeof target.score === 'number' ? target.score : null,
    startedAt: target.startedAt || null,
    completedAt: target.completedAt || null,
    attempts: target.attempts || [], // Include attempts history
    participantId: participant
      ? {
          _id: participant._id,
          name: participant.name || '',
          surname: participant.surname || '',
          email: participant.email || '',
          department: participant.department || '',
          businessUnit: businessUnit?.name
            ? {
                _id: businessUnit._id,
                name: businessUnit.name
              }
            : null,
          phoneNumber: phone.full || phone.countryCode || phone.number
            ? {
                full: phone.full || '',
                countryCode: phone.countryCode || '',
                number: phone.number || ''
              }
            : null
        }
      : null,
    smsInvitation: target.smsInvitation || target?.invitations?.sms || null,
    whatsappInvitation: target.whatsappInvitation || target?.invitations?.whatsapp || null,
    includeInResults: target.includeInResults !== false, // Default true
    sessionData: target.sessionData || null,
  };
};

// Optimized override/search modal
const MessagingModal = React.memo(({ open, mode, target, requiresOverride, onClose, onSent }) => {
  const [overrideEnabled, setOverrideEnabled] = useState(requiresOverride);
  const [overrideContact, setOverrideContact] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const deferredSearch = useDeferredValue(searchQuery);
  const [isSearchPending, startSearchTransition] = useTransition();
  const participantName = getParticipantName(target?.participantId);
  const participantPhone = getParticipantPhone(target?.participantId);
  const targetId = target?._id || null;

  useEffect(() => {
    if (!open) {
      return;
    }
    setOverrideEnabled(requiresOverride);
    setOverrideContact(null);
    setSearchInput('');
    setSearchQuery('');
    setSearchResults([]);
    setSearchLoading(false);
  }, [open, requiresOverride, targetId]);

  useEffect(() => {
    if (!open || !overrideEnabled) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    const query = deferredSearch.trim();
    if (!query || query.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await smsService.searchUsers(query);
        if (cancelled) {
          return;
        }
        if (response.success) {
          const list = Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : [];
          setSearchResults(list);
        } else {
          setSearchResults([]);
          message.error(response.message || 'Failed to search contacts');
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Override search error:', error);
          setSearchResults([]);
          message.error('Failed to search contacts');
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, overrideEnabled, deferredSearch]);

  const updateSearchQuery = useCallback((value, options = {}) => {
    const { force = false } = options;
    const trimmed = value.trim();

    startSearchTransition(() => {
      if (!trimmed) {
        setSearchQuery('');
        return;
      }

      if (trimmed.length < 3 && !force) {
        setSearchQuery('');
        return;
      }

      setSearchQuery(trimmed);
    });
  }, [startSearchTransition]);

  const handleOverrideToggle = useCallback((checked) => {
    if (requiresOverride && !checked) {
      return;
    }
    setOverrideEnabled(checked);
    if (!checked) {
      setOverrideContact(null);
      setSearchInput('');
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [requiresOverride]);

  const handleOverrideSearchInputChange = useCallback((value) => {
    setSearchInput(value);
    updateSearchQuery(value);
  }, [updateSearchQuery]);

  const handleOverrideSearchSubmit = useCallback((value) => {
    setSearchInput(value);
    updateSearchQuery(value, { force: true });
  }, [updateSearchQuery]);

  const handleOverrideSelect = useCallback((contact) => {
    if (!contact?.phoneNumber) {
      message.error('Selected contact has no phone number');
      return;
    }
    setOverrideContact(contact);
    if (!overrideEnabled) {
      setOverrideEnabled(true);
    }
  }, [overrideEnabled]);

  const handleSendMessage = useCallback(async () => {
    if (!targetId) {
      return;
    }

    const overrideRecipient = overrideEnabled && overrideContact?.phoneNumber
      ? {
          fullName:
            overrideContact.fullName
            || `${overrideContact.name || ''} ${overrideContact.surname || ''}`.trim()
            || overrideContact.email
            || 'Recipient',
          phoneNumber: overrideContact.phoneNumber
        }
      : null;

    if (requiresOverride && !overrideRecipient) {
      message.warning('This invitation was already sent. Select an override recipient to resend.');
      return;
    }

    if (!overrideRecipient && !participantPhone) {
      message.error('This participant does not have a phone number on record');
      return;
    }

    setSending(true);
    try {
      let response;
      if (mode === 'sms') {
        response = await smsService.sendCampaignTargetSMS(targetId, overrideRecipient || null);
      } else {
        response = await whatsappService.sendCampaignTargetInvitation(targetId, overrideRecipient || null);
      }

      if (response.success) {
        message.success(response.message || `Invitation sent via ${mode === 'sms' ? 'SMS' : 'WhatsApp'}`);
        onClose();
        if (onSent) {
          onSent();
        }
      } else {
        message.error(response.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Messaging send error:', error);
      message.error('Failed to send invitation');
    } finally {
      setSending(false);
    }
  }, [mode, onClose, onSent, overrideContact, overrideEnabled, participantPhone, requiresOverride, targetId]);

  const showMissingPhoneAlert = !participantPhone && !overrideEnabled;
  const modeLabel = mode === 'sms' ? 'SMS' : 'WhatsApp';
  const canSend = overrideEnabled
    ? Boolean(overrideContact?.phoneNumber)
    : Boolean(participantPhone);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      okText={`Send ${modeLabel}`}
      onOk={handleSendMessage}
      confirmLoading={sending}
      okButtonProps={{ disabled: !canSend }}
      title={`${modeLabel} Invitation`}
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Text type="secondary">Original participant</Text>
          <div style={{ marginTop: 4 }}>
            <Text strong style={{ display: 'block' }}>{participantName}</Text>
            <Text type={participantPhone ? 'secondary' : 'danger'}>
              {participantPhone || 'No phone number available'}
            </Text>
          </div>
        </div>

        {showMissingPhoneAlert && (
          <Alert
            type="warning"
            showIcon
            message="This participant does not have a phone number on record"
            description="Enable the override option below to choose a different recipient."
          />
        )}

        {requiresOverride && (
          <Alert
            type="info"
            showIcon
            message="Override required to resend"
            description="This invitation has already been delivered once. Choose another recipient to send manually."
          />
        )}

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              size="small"
              checked={overrideEnabled}
              disabled={requiresOverride}
              onChange={handleOverrideToggle}
            />
            <Text>Override recipient</Text>
          </div>
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            {requiresOverride
              ? 'Select a teammate below and send the reminder to their number instead.'
              : 'Search for another team member and temporarily use their number instead.'}
          </Text>
        </div>

        {overrideEnabled && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Search
              placeholder="Search by name, email, or phone number"
              value={searchInput}
              onChange={(e) => handleOverrideSearchInputChange(e.target.value)}
              allowClear
              enterButton="Search"
              onSearch={handleOverrideSearchSubmit}
              loading={searchLoading || isSearchPending}
            />

            {overrideContact && (
              <Alert
                type="info"
                showIcon
                message={`Override recipient: ${overrideContact.fullName || `${overrideContact.name || ''} ${overrideContact.surname || ''}`.trim() || overrideContact.email || overrideContact.phoneNumber}`}
                description={
                  <span>
                    {overrideContact.email && `${overrideContact.email} • `}
                    {overrideContact.phoneNumber}
                  </span>
                }
                action={
                  <Button type="link" size="small" onClick={() => setOverrideContact(null)}>
                    Clear
                  </Button>
                }
              />
            )}

            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              <List
                size="small"
                bordered
                loading={searchLoading || isSearchPending}
                dataSource={searchResults}
                locale={{
                  emptyText: searchQuery
                    ? 'No contacts found'
                    : 'Type at least 3 characters or press enter to search'
                }}
                renderItem={(item) => {
                  const title = item.fullName || `${item.name || ''} ${item.surname || ''}`.trim() || item.email || 'Contact';
                  const isSelected = overrideContact
                    ? (overrideContact._id && item._id
                        ? overrideContact._id === item._id
                        : overrideContact.phoneNumber === item.phoneNumber)
                    : false;
                  return (
                    <List.Item
                      key={item._id || item.phoneNumber || title}
                      actions={[
                        <Button
                          type={isSelected ? 'primary' : 'link'}
                          onClick={() => handleOverrideSelect(item)}
                          size="small"
                          key="select"
                        >
                          {isSelected ? 'Selected' : 'Use'}
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        title={title}
                        description={(
                          <span>
                            {item.email || 'No email'}
                            {' • '}
                            {item.phoneNumber || 'No phone'}
                          </span>
                        )}
                      />
                    </List.Item>
                  );
                }}
              />
            </div>
          </Space>
        )}
      </Space>
    </Modal>
  );
});

const CampaignReport = () => {
  const { id } = useParams();
  const location = useLocation();
  const { token } = theme.useToken(); // Move theme hook to top of component
  const [campaign, setCampaign] = useState(location.state?.campaign || null);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [campaignOptionsLoading, setCampaignOptionsLoading] = useState(false);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState(id ? [id] : []);
  const [targets, setTargets] = useState([]);
  const [apiTargetTotal, setApiTargetTotal] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [statistics, setStatistics] = useState(null);
  const [questionAnalysis, setQuestionAnalysis] = useState(null);
  const [multiAggregates, setMultiAggregates] = useState(null);
  const [multiLoading, setMultiLoading] = useState(false);
  const [multiError, setMultiError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [multiDateRange, setMultiDateRange] = useState([null, null]);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [answersModalVisible, setAnswersModalVisible] = useState(false);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [participantAnswers, setParticipantAnswers] = useState(null);

  // Export state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFields, setExportFields] = useState(() => ALL_EXPORT_FIELDS.filter(f => f.default).map(f => f.key));
  const [attemptsModalVisible, setAttemptsModalVisible] = useState(false);
  const [attemptsModalTarget, setAttemptsModalTarget] = useState(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageModalMode, setMessageModalMode] = useState('sms');
  const [messageModalTarget, setMessageModalTarget] = useState(null);
  const [messageModalRequiresOverride, setMessageModalRequiresOverride] = useState(false);
  const [departmentMetricsMode, setDepartmentMetricsMode] = useState('percent');
  const [isEditingEndDate, setIsEditingEndDate] = useState(false);
  const [newEndDate, setNewEndDate] = useState(null);
  const [updatingEndDate, setUpdatingEndDate] = useState(false);
  const [isEditingLeaderboard, setIsEditingLeaderboard] = useState(false);
  const [newLeaderboardType, setNewLeaderboardType] = useState('none');
  const [updatingLeaderboard, setUpdatingLeaderboard] = useState(false);
  
  // AI Insights state
  const [aiInsightModalOpen, setAiInsightModalOpen] = useState(false);
  const [aiInsightLoading, setAiInsightLoading] = useState(false);
  const [aiInsightStatus, setAiInsightStatus] = useState('');
  const [aiInsight, setAiInsight] = useState(null);
  const [aiSessionId, setAiSessionId] = useState(null);
  const [aiFollowUpMessages, setAiFollowUpMessages] = useState([]);
  const [aiFollowUpInput, setAiFollowUpInput] = useState('');
  const [aiFollowUpLoading, setAiFollowUpLoading] = useState(false);
  const aiChatEndRef = useRef(null);

  // Check if campaign can have its end date edited (not closed or completed)
  const canEditEndDate = campaign && !['closed', 'completed'].includes(campaign.status);

  // Handler for updating campaign end date
  const handleUpdateEndDate = async () => {
    if (!newEndDate || !campaign?._id) return;
    
    try {
      setUpdatingEndDate(true);
      const response = await campaignService.update(campaign._id, { endDate: newEndDate.toISOString() });
      
      if (response.success) {
        setCampaign(prev => ({ ...prev, endDate: newEndDate.toISOString() }));
        message.success('End date updated successfully');
        setIsEditingEndDate(false);
        setNewEndDate(null);
      } else {
        message.error(response.message || 'Failed to update end date');
      }
    } catch (error) {
      console.error('Error updating end date:', error);
      message.error('Failed to update end date');
    } finally {
      setUpdatingEndDate(false);
    }
  };

  // Handler for updating leaderboard type
  const handleUpdateLeaderboard = async () => {
    if (!campaign?._id) return;
    
    try {
      setUpdatingLeaderboard(true);
      const response = await campaignService.update(campaign._id, { 
        settings: { ...campaign.settings, leaderboardType: newLeaderboardType } 
      });
      
      if (response.success) {
        setCampaign(prev => ({ 
          ...prev, 
          settings: { ...prev.settings, leaderboardType: newLeaderboardType } 
        }));
        message.success('Leaderboard setting updated');
        setIsEditingLeaderboard(false);
      } else {
        message.error(response.message || 'Failed to update leaderboard');
      }
    } catch (error) {
      console.error('Error updating leaderboard:', error);
      message.error('Failed to update leaderboard');
    } finally {
      setUpdatingLeaderboard(false);
    }
  };

  // Handler for getting AI insights — streams tokens into the insight text
  const handleGetAIInsights = async () => {
    if (!id) return;

    setAiInsightModalOpen(true);
    setAiInsightLoading(true);
    setAiInsightStatus('');
    setAiInsight(null);
    setAiSessionId(null);
    setAiFollowUpMessages([]);
    setAiFollowUpInput('');

    let accumulated = '';

    await campaignService.getCampaignAIInsights(id, {
      onStatus: (msg) => setAiInsightStatus(msg),
      onToken: (token) => {
        accumulated += token;
        setAiInsightLoading(false);
        setAiInsightStatus('');
        setAiInsight(prev => ({ ...(prev || {}), insight: accumulated }));
      },
      onDone: (meta) => {
        if (meta.sessionId) setAiSessionId(meta.sessionId);
        if (meta.stats) setAiInsight(prev => ({ ...(prev || {}), stats: meta.stats }));
        setAiInsightLoading(false);
      },
      onError: (err) => {
        message.error(err || 'Failed to get AI insights');
        setAiInsightModalOpen(false);
        setAiInsightLoading(false);
      }
    });
  };

  const handleAskAIFollowUp = async () => {
    const question = aiFollowUpInput.trim();
    if (!question || !aiSessionId || aiFollowUpLoading) return;

    setAiFollowUpInput('');
    setAiFollowUpMessages(prev => [...prev, { role: 'user', content: question }]);
    setAiFollowUpLoading(true);

    // Add an empty assistant placeholder that we'll fill as tokens arrive
    setAiFollowUpMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    await campaignService.askCampaignAIQuestion(id, aiSessionId, question, {
      onToken: (token) => {
        setAiFollowUpMessages(prev => {
          const msgs = [...prev];
          const last = msgs[msgs.length - 1];
          if (last?.role === 'assistant') msgs[msgs.length - 1] = { ...last, content: last.content + token };
          return msgs;
        });
        setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      },
      onDone: () => {
        setAiFollowUpLoading(false);
        setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      },
      onError: (err) => {
        if (err === 'expired') {
          setAiSessionId(null);
          message.warning('Session expired. Please generate new insights to continue.');
        }
        setAiFollowUpMessages(prev => {
          const msgs = [...prev];
          const last = msgs[msgs.length - 1];
          if (last?.role === 'assistant' && !last.content) {
            msgs[msgs.length - 1] = { ...last, content: err === 'expired' ? 'Session expired.' : 'An error occurred. Please try again.' };
          }
          return msgs;
        });
        setAiFollowUpLoading(false);
      }
    });
  };

  const handleCloseAIInsightModal = () => {
    if (aiSessionId) {
      campaignService.endCampaignAISession(id, aiSessionId);
    }
    setAiInsightModalOpen(false);
    setAiSessionId(null);
    setAiFollowUpMessages([]);
    setAiFollowUpInput('');
  };

  // Define panel style for collapse items
  const panelStyle = {
    marginBottom: 24,
    background: token.colorFillAlter,
    borderRadius: token.borderRadiusLG,
    border: 'none',
  };

  // Load campaign options for multi-campaign reporting
  const loadCampaignOptions = useCallback(async (dateRange = [null, null]) => {
    try {
      setCampaignOptionsLoading(true);
      const params = { limit: 200, status: 'all' };
      
      // Add date range filter if provided
      if (dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].toISOString();
        params.endDate = dateRange[1].toISOString();
      }
      
      const response = await campaignService.getAll(params);
      if (response.success && Array.isArray(response.data)) {
        setAvailableCampaigns(response.data);
      }
    } catch (error) {
      console.error('Error loading campaign options:', error);
    } finally {
      setCampaignOptionsLoading(false);
    }
  }, []);

  // Fetch only targets (limited batch for searchability)
  const fetchTargets = useCallback(async (page = 1, search = '', status = 'all', extraFilters = {}, pageSizeOverride) => {
    try {
      setTargetsLoading(true);
      const effectivePageSize = pageSizeOverride || pageSize;
      
      // Build query parameters
      const params = {
        page,
        limit: effectivePageSize,
        fields: TARGET_FIELD_SELECTION,
        ...(status !== 'all' && { status }),
        ...(search && { search }),
        ...extraFilters
      };
      
      // Fetch campaign targets with search parameters
      const targetsResponse = await campaignService.getCampaignTargets(id, params);
      
      if (targetsResponse.success) {
        const rawTargets = targetsResponse.data.targets || [];
        const paginationTotal = targetsResponse.data.pagination?.total ?? rawTargets.length;
        console.log('[CampaignReport] API returned', rawTargets.length, 'targets, pagination total:', paginationTotal);
        const optimizedTargets = rawTargets.map(simplifyTargetRecord);
        setTargets(optimizedTargets);
        setApiTargetTotal(paginationTotal);
        setCurrentPage(page);
      } else {
        message.error(targetsResponse.message || 'Failed to load targets');
      }
    } catch (error) {
      console.error('Error fetching targets:', error);
      message.error('Failed to load targets');
    } finally {
      setTargetsLoading(false);
    }
  }, [id, pageSize]);
  const fetchCampaignData = useCallback(async () => {
    try {
      setLoading(true);

      // Parse filters from URL query
      const qs = new URLSearchParams(location.search || '');
      const start = qs.get('start');
      const end = qs.get('end');
      const channel = qs.get('channel');
      const statusQ = qs.get('status');
      const targetsJson = qs.get('targets');
      let targetsFilter = {};
      if (targetsJson) {
        try {
          const arr = JSON.parse(targetsJson);
          if (Array.isArray(arr) && arr.length > 0) {
            targetsFilter = { targets: arr };
          }
        } catch {}
      }

      const statsParams = {
        ...(start && { start }),
        ...(end && { end }),
        ...(channel && channel !== 'all' && { channel }),
        ...(statusQ && statusQ !== 'all' && { status: statusQ }),
        ...targetsFilter
      };

      const dataPromise = Promise.allSettled([
        campaignService.getById(id, { fields: CAMPAIGN_SUMMARY_FIELDS }),
        campaignService.getCampaignStatistics(id, statsParams),
        campaignService.getCampaignQuestionAnalysis(id, statsParams)
      ]);

      const [results] = await Promise.all([
        dataPromise,
        fetchTargets(1, '', statusQ || 'all', statsParams)
      ]);

      const [campaignResult, statsResult, analysisResult] = results;

      if (campaignResult?.status === 'fulfilled' && campaignResult.value?.success) {
        setCampaign(campaignResult.value.data);
      }

      if (statsResult?.status === 'fulfilled' && statsResult.value?.success) {
        setStatistics(statsResult.value.data);
      }

      if (analysisResult?.status === 'fulfilled' && analysisResult.value?.success) {
        setQuestionAnalysis(analysisResult.value.data);
      }
      
    } catch (error) {
      console.error('Error fetching campaign data:', error);
      message.error('Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  }, [id, fetchTargets, location.search]);

  useEffect(() => {
    if (id) {
      // Initialize search state and load all campaign data
      setSearchText('');
      const qs = new URLSearchParams(location.search || '');
      const statusQ = qs.get('status');
      setStatusFilter(statusQ || 'all');
      fetchCampaignData();
    }
  }, [id, fetchCampaignData, location.search]);

  useEffect(() => {
    loadCampaignOptions(multiDateRange);
  }, [loadCampaignOptions, multiDateRange]);

  useEffect(() => {
    if (id) {
      setSelectedCampaignIds([id]);
    }
  }, [id]);

  // Search handlers - only fetch targets, don't reload entire report
  const handleSearch = (value) => {
    setSearchText(value);
    fetchTargets(1, value, statusFilter);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    fetchTargets(1, searchText, value);
  };

  const handleTablePageChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      fetchTargets(1, searchText, statusFilter, {}, size);
    } else {
      fetchTargets(page, searchText, statusFilter);
    }
  };

  // Map backend statistics to frontend format
  const mapBackendStats = (backendStats) => {
    if (!backendStats?.overview) return null;
    
    const overview = backendStats.overview;
    
    return {
      totalTargets: overview.total || 0,
      completed: overview.completed || 0,
      inProgress: overview.inProgress || 0,
      pending: overview.pending || 0,
      expired: overview.expired || 0,
      excluded: overview.excluded || 0,
      averageScore: Math.round((overview.averageScore || 0) * 100) / 100,
      averageCompletionTime: Math.round((overview.averageTimeSpent || 0) * 100) / 100,
      completionRate: overview.total > 0 ? Math.round((overview.completed / overview.total) * 100) : 0
    };
  };

  const stats = useMemo(() => mapBackendStats(statistics) || {
    totalTargets: 0,
    completed: 0,
    inProgress: 0,
    pending: 0,
    averageCompletionTime: 0,
    averageScore: 0,
    completionRate: 0
  }, [statistics]);

  const messagingStats = useMemo(() => ({
    sms: {
      sent: statistics?.overview?.sms?.sent || 0,
      delivered: statistics?.overview?.sms?.delivered || 0,
      failed: statistics?.overview?.sms?.failed || 0,
      pending: statistics?.overview?.sms?.pending || 0,
      deliveryRate: statistics?.overview?.sms?.deliveryRate || '0%'
    },
    whatsapp: {
      sent: statistics?.overview?.whatsapp?.sent || 0,
      success: statistics?.overview?.whatsapp?.success || 0,
      failure: statistics?.overview?.whatsapp?.failure || 0,
      pending: statistics?.overview?.whatsapp?.pending || 0,
      successRate: statistics?.overview?.whatsapp?.successRate || '0%'
    }
  }), [statistics]);

  const smsDeliveryData = useMemo(() => ([
    { stage: 'Sent', value: messagingStats.sms.sent },
    { stage: 'Delivered', value: messagingStats.sms.delivered },
    { stage: 'Pending', value: messagingStats.sms.pending },
    { stage: 'Failed', value: messagingStats.sms.failed }
  ]), [messagingStats]);

  const whatsappDeliveryData = useMemo(() => ([
    { stage: 'Sent', value: messagingStats.whatsapp.sent },
    { stage: 'Delivered', value: messagingStats.whatsapp.success },
    { stage: 'Pending', value: messagingStats.whatsapp.pending },
    { stage: 'Failed', value: messagingStats.whatsapp.failure }
  ]), [messagingStats]);

  const formatTooltipValue = (value, total) => {
    if (total <= 0) return `${value}`;
    const percentage = ((value / total) * 100).toFixed(1);
    return `${value} (${percentage}%)`;
  };

  const formatDuration = (input, options = {}) => {
    const { unit = 'minutes', fallback = '0s', includeSeconds = true } = options;
    const numeric = Number(input);

    if (!Number.isFinite(numeric)) {
      return fallback;
    }

    let totalSeconds;
    switch (unit) {
      case 'milliseconds':
        totalSeconds = Math.max(0, Math.round(numeric / 1000));
        break;
      case 'seconds':
        totalSeconds = Math.max(0, Math.round(numeric));
        break;
      default:
        totalSeconds = Math.max(0, Math.round(numeric * 60));
    }

    if (totalSeconds === 0) {
      return includeSeconds ? '0s' : fallback;
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts = [];
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (includeSeconds && (seconds || parts.length === 0)) {
      parts.push(`${seconds}s`);
    }

    return parts.length ? parts.join(' ') : fallback;
  };

  const normalizePercent = useCallback((input) => {
    const visited = new Set();

    const resolve = (value) => {
      if (value === null || value === undefined) {
        return Number.NaN;
      }

      if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
          return Number.NaN;
        }
        if (value > 1 || value < -1) {
          return value;
        }
        if (value === 0) {
          return 0;
        }
        return value * 100;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
          return Number.NaN;
        }
        const hadPercent = trimmed.includes('%');
        const cleaned = trimmed.replace(/%/g, '');
        const parsed = Number(cleaned);
        if (!Number.isFinite(parsed)) {
          return Number.NaN;
        }
        if (hadPercent || Math.abs(parsed) > 1) {
          return parsed;
        }
        if (parsed === 0) {
          return 0;
        }
        return parsed * 100;
      }

      if (typeof value === 'object') {
        if (visited.has(value)) {
          return Number.NaN;
        }
        visited.add(value);

        const displayFields = ['display', 'displayValue', 'formatted', 'label'];
        for (const field of displayFields) {
          const fieldValue = value[field];
          if (typeof fieldValue === 'string' && fieldValue.trim()) {
            const resolved = resolve(fieldValue);
            if (Number.isFinite(resolved)) {
              return resolved;
            }
          }
        }

        const candidateFields = ['value', 'percentage', 'percent', 'rate', 'raw', 'rawValue', 'score'];
        for (const field of candidateFields) {
          if (value[field] !== undefined) {
            const resolved = resolve(value[field]);
            if (Number.isFinite(resolved)) {
              return resolved;
            }
          }
        }

        if (typeof value.toString === 'function' && value.toString !== Object.prototype.toString) {
          const asString = value.toString();
          const resolved = resolve(asString);
          if (Number.isFinite(resolved)) {
            return resolved;
          }
        }

        return Number.NaN;
      }

      return Number.NaN;
    };

    const numeric = resolve(input);
    return Number.isFinite(numeric) ? numeric : 0;
  }, []);

  const coerceRate = useCallback((value) => normalizePercent(value), [normalizePercent]);

  const getRateDisplay = useCallback((value) => {
    if (value === null || value === undefined) {
      return '0%';
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return '0%';
      }
      return trimmed.includes('%') ? trimmed : `${trimmed}%`;
    }

    if (typeof value === 'object') {
      const displayFields = ['display', 'displayValue', 'formatted', 'label'];
      for (const field of displayFields) {
        const fieldValue = value[field];
        if (typeof fieldValue === 'string' && fieldValue.trim()) {
          return fieldValue.includes('%') ? fieldValue : `${fieldValue}%`;
        }
      }
    }

    const numeric = normalizePercent(value);
    if (!Number.isFinite(numeric) || numeric === 0) {
      return '0%';
    }
    const digits = Number.isInteger(numeric) ? 0 : 1;
    return `${numeric.toFixed(digits)}%`;
  }, [normalizePercent]);

  const getChartValueLabel = useCallback((source) => {
    if (source === null || source === undefined) {
      return '0%';
    }

    if (typeof source === 'string' || typeof source === 'number') {
      return getRateDisplay(source);
    }

    if (typeof source === 'object') {
      if (source.displayValue) {
        return source.displayValue;
      }
      if (source.value !== undefined) {
        return getRateDisplay(source.value);
      }
      if (source.data?.displayValue) {
        return source.data.displayValue;
      }
      if (source.data?.value !== undefined) {
        return getRateDisplay(source.data.value);
      }
    }

    return '0%';
  }, [getRateDisplay]);

  const formatPercentDisplay = useCallback((value, digits = 0) => {
    const numeric = normalizePercent(value);
    if (!Number.isFinite(numeric)) {
      return '0%';
    }
    // Cap at maximum 2 decimal places for readability
    const maxDigits = Math.min(digits, 2);
    return `${numeric.toFixed(maxDigits)}%`;
  }, [normalizePercent]);

  const formatPercentAxis = useCallback((value, digits = 2) => {
    const numeric = normalizePercent(value);
    if (!Number.isFinite(numeric)) {
      return '0%';
    }
    if (Number.isInteger(numeric)) {
      return `${numeric}%`;
    }
    return `${numeric.toFixed(digits)}%`;
  }, [normalizePercent]);

  const formatNumberDisplay = useCallback((value, digits = 0) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '0';
    }
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  }, []);

  const formatCountDisplay = useCallback((count, total) => {
    const numericCount = Number(count);
    const numericTotal = Number(total);
    const safeCount = Number.isFinite(numericCount) ? numericCount : 0;
    if (Number.isFinite(numericTotal) && numericTotal > 0) {
      return `${safeCount}/${numericTotal}`;
    }
    return `${safeCount}`;
  }, []);

  const formatCountAxis = useCallback((value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '0';
    }
    if (Math.abs(numeric) >= 1000) {
      return numeric.toLocaleString();
    }
    if (Number.isInteger(numeric)) {
      return `${numeric}`;
    }
    return numeric.toFixed(1);
  }, []);

  // ---- Multi-campaign aggregation helpers ----
  const buildAggregatedOverview = useCallback((items = []) => {
    const aggregate = {
      totalTargets: 0,
      uniqueEmployees: 0,
      completed: 0,
      inProgress: 0,
      pending: 0,
      expired: 0,
      excluded: 0,
      averageScore: 0,
      completionRate: 0,
      responseRate: 0,
      sms: { sent: 0, delivered: 0, failed: 0, pending: 0, deliveryRate: '0%' },
      whatsapp: { sent: 0, success: 0, failure: 0, pending: 0, successRate: '0%' }
    };

    let weightedScoreSum = 0;
    let weightedScoreCount = 0;
    const uniqueParticipantIds = new Set();

    items.forEach((item) => {
      const overview = item?.stats?.overview || {};
      aggregate.totalTargets += overview.total || 0;
      aggregate.completed += overview.completed || 0;
      aggregate.inProgress += overview.inProgress || 0;
      aggregate.pending += overview.pending || 0;
      aggregate.expired += overview.expired || 0;
      aggregate.excluded += overview.excluded || 0;

      // Track unique participants
      const targets = item?.targets || [];
      targets.forEach((target) => {
        const participantId = target?.participantId?._id || target?.participantId?.email;
        if (participantId) {
          uniqueParticipantIds.add(participantId);
        }
      });

      const completedCount = overview.completed || 0;
      const avgScore = Number(overview.averageScore) || 0;
      weightedScoreSum += avgScore * completedCount;
      weightedScoreCount += completedCount;

      aggregate.sms.sent += overview.sms?.sent || 0;
      aggregate.sms.delivered += overview.sms?.delivered || 0;
      aggregate.sms.failed += overview.sms?.failed || 0;
      aggregate.sms.pending += overview.sms?.pending || 0;

      aggregate.whatsapp.sent += overview.whatsapp?.sent || 0;
      aggregate.whatsapp.success += overview.whatsapp?.success || 0;
      aggregate.whatsapp.failure += overview.whatsapp?.failure || 0;
      aggregate.whatsapp.pending += overview.whatsapp?.pending || 0;
    });

    // Set unique employee count
    aggregate.uniqueEmployees = uniqueParticipantIds.size;

    aggregate.averageScore = weightedScoreCount > 0 ? (weightedScoreSum / weightedScoreCount) : 0;
    aggregate.completionRate = aggregate.totalTargets > 0
      ? (aggregate.completed / aggregate.totalTargets) * 100
      : 0;
    aggregate.responseRate = aggregate.totalTargets > 0
      ? ((aggregate.completed + aggregate.inProgress) / aggregate.totalTargets) * 100
      : 0;

    aggregate.sms.deliveryRate = aggregate.sms.sent > 0
      ? `${((aggregate.sms.delivered / aggregate.sms.sent) * 100).toFixed(1)}%`
      : '0%';
    aggregate.whatsapp.successRate = aggregate.whatsapp.sent > 0
      ? `${((aggregate.whatsapp.success / aggregate.whatsapp.sent) * 100).toFixed(1)}%`
      : '0%';

    return aggregate;
  }, []);

  const buildAggregatedOrganizations = useCallback((items = []) => {
    const orgMap = new Map();

    items.forEach((item) => {
      (item?.stats?.byOrganization || []).forEach((org) => {
        const orgName = org?._id?.organizationName || org?._id || org?.organizationName || 'Organization';
        const participants = org?.totalParticipants || org?.participants || 0;
        const completed = org?.totalCompleted || org?.completed || 0;

        let avgScore = Number(org?.averageScore);
        let scoreWeight = completed;

        if (!Number.isFinite(avgScore) && Array.isArray(org?.departments)) {
          let sum = 0;
          let comp = 0;
          org.departments.forEach((dept) => {
            const deptCompleted = dept?.completed || dept?.completedCount || 0;
            const deptScore = Number(dept?.averageScore) || 0;
            sum += deptScore * deptCompleted;
            comp += deptCompleted;
          });
          avgScore = comp > 0 ? (sum / comp) : 0;
          scoreWeight = comp;
        }

        const existing = orgMap.get(orgName) || {
          organization: orgName,
          participants: 0,
          completed: 0,
          scoreSum: 0,
          scoreWeight: 0
        };

        existing.participants += participants;
        existing.completed += completed;
        existing.scoreSum += (Number.isFinite(avgScore) ? avgScore : 0) * scoreWeight;
        existing.scoreWeight += scoreWeight;

        orgMap.set(orgName, existing);
      });
    });

    return Array.from(orgMap.values())
      .map((org) => {
        const completionRate = org.participants > 0 ? (org.completed / org.participants) * 100 : 0;
        const averageScore = org.scoreWeight > 0 ? (org.scoreSum / org.scoreWeight) : 0;
        return {
          ...org,
          completionRate,
          averageScore,
          completionRateDisplay: formatPercentDisplay(completionRate, 2),
          averageScoreDisplay: formatPercentDisplay(averageScore, 2)
        };
      })
      .sort((a, b) => (b.averageScore || 0) - (a.averageScore || 0));
  }, [formatPercentDisplay]);

  const buildAggregatedDepartments = useCallback((items = []) => {
    const deptMap = new Map();

    items.forEach((item) => {
      (item?.stats?.byDepartment || []).forEach((dept) => {
        const name = dept?.department || 'Unassigned';
        const participants = dept?.totalParticipants || 0;
        const responded = dept?.respondedCount || 0;
        const completed = dept?.completedCount || 0;
        const averageScore = Number(dept?.averageScore) || 0;

        const existing = deptMap.get(name) || {
          department: name,
          totalParticipants: 0,
          respondedCount: 0,
          completedCount: 0,
          scoreSum: 0,
          scoreWeight: 0
        };

        existing.totalParticipants += participants;
        existing.respondedCount += responded;
        existing.completedCount += completed;
        existing.scoreSum += averageScore * completed;
        existing.scoreWeight += completed;

        deptMap.set(name, existing);
      });
    });

    return Array.from(deptMap.values()).map((dept) => {
      const responseRate = dept.totalParticipants > 0 ? (dept.respondedCount / dept.totalParticipants) * 100 : 0;
      const completionRate = dept.totalParticipants > 0 ? (dept.completedCount / dept.totalParticipants) * 100 : 0;
      const averageScore = dept.scoreWeight > 0 ? (dept.scoreSum / dept.scoreWeight) : 0;
      return {
        ...dept,
        responseRate,
        completionRate,
        averageScore,
        responseRateDisplay: formatPercentDisplay(responseRate, 2),
        completionRateDisplay: formatPercentDisplay(completionRate, 2),
        averageScoreDisplay: formatPercentDisplay(averageScore, 2)
      };
    }).sort((a, b) => (b.responseRate || 0) - (a.responseRate || 0));
  }, [formatPercentDisplay]);

  const buildAggregatedDepartmentGroups = useCallback((departments = []) => {
    if (!departments.length) return [];
    const grouped = new Map();

    departments.forEach((dept) => {
      const group = resolveDepartmentGroup(dept?.department);
      const key = group?.key || 'other';
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          label: group?.label || 'Other Departments',
          departments: new Set(),
          totalParticipants: 0,
          respondedCount: 0,
          completedCount: 0,
          scoreSum: 0,
          scoreWeight: 0
        });
      }
      const entry = grouped.get(key);
      if (dept?.department) {
        entry.departments.add(dept.department);
      }
      entry.totalParticipants += dept?.totalParticipants || 0;
      entry.respondedCount += dept?.respondedCount || 0;
      entry.completedCount += dept?.completedCount || 0;
      entry.scoreSum += (dept?.averageScore || 0) * (dept?.completedCount || 0);
      entry.scoreWeight += dept?.completedCount || 0;
    });

    return Array.from(grouped.values()).map((group) => {
      const responseRate = group.totalParticipants > 0 ? (group.respondedCount / group.totalParticipants) * 100 : 0;
      const completionRate = group.totalParticipants > 0 ? (group.completedCount / group.totalParticipants) * 100 : 0;
      const averageScore = group.scoreWeight > 0 ? (group.scoreSum / group.scoreWeight) : 0;
      return {
        ...group,
        departments: Array.from(group.departments),
        responseRate,
        completionRate,
        averageScore,
        responseRateDisplay: formatPercentDisplay(responseRate, 2),
        completionRateDisplay: formatPercentDisplay(completionRate, 2),
        averageScoreDisplay: formatPercentDisplay(averageScore, 2)
      };
    }).sort((a, b) => (b.completedCount || 0) - (a.completedCount || 0));
  }, [formatPercentDisplay]);

  const buildAggregatedQuestionInsights = useCallback((items = []) => {
    const allQuestions = [];

    items.forEach((item) => {
      const label = item?.name || 'Campaign';
      const q = item?.questions || {};
      
      // Collect all questions from analysis
      if (q.analysis && Array.isArray(q.analysis)) {
        q.analysis.forEach((question) => {
          if (question && typeof question.failureRate === 'number') {
            allQuestions.push({
              questionText: question.questionText || 'Question',
              failureRate: question.failureRate,
              successRate: question.successRate || (100 - question.failureRate),
              correctAnswers: question.correctAnswers || 0,
              totalAnswers: question.totalAnswers || 0,
              campaign: label
            });
          }
        });
      }
      
      // Also include mostDifficult if exists
      if (q.mostDifficult && typeof q.mostDifficult.failureRate === 'number') {
        allQuestions.push({
          questionText: q.mostDifficult.questionText || 'Question',
          failureRate: q.mostDifficult.failureRate,
          successRate: q.mostDifficult.successRate || (100 - q.mostDifficult.failureRate),
          correctAnswers: q.mostDifficult.correctAnswers || 0,
          totalAnswers: q.mostDifficult.totalAnswers || 0,
          campaign: label
        });
      }
    });

    // Sort by failure rate descending (most difficult first) and get top 10
    const top10Difficult = allQuestions
      .sort((a, b) => b.failureRate - a.failureRate)
      .slice(0, 10);

    return {
      top10Difficult,
      hardest: top10Difficult[0] || null,
      easiest: allQuestions.length > 0 
        ? allQuestions.sort((a, b) => a.failureRate - b.failureRate)[0]
        : null
    };
  }, []);

  const fetchMultiCampaignStats = useCallback(async (campaignIds = [], orgFilter = [], dateRange = [null, null]) => {
    if (!campaignIds.length) {
      setMultiAggregates(null);
      return;
    }

    setMultiLoading(true);
    setMultiError(null);

    try {
      // Build filter parameters
      const filterParams = {};
      if (orgFilter.length > 0) {
        filterParams.organizations = orgFilter.join(',');
      }
      if (dateRange[0] && dateRange[1]) {
        filterParams.startDate = dateRange[0].toISOString();
        filterParams.endDate = dateRange[1].toISOString();
      }

      const results = await Promise.allSettled(
        campaignIds.map(async (campaignId) => {
          const [statsResult, questionsResult, targetsResult] = await Promise.allSettled([
            campaignService.getCampaignStatistics(campaignId, filterParams),
            campaignService.getCampaignQuestionAnalysis(campaignId, filterParams),
            campaignService.getCampaignTargets(campaignId, { 
              fields: 'participantId._id,participantId.email',
              limit: 10000,
              ...filterParams
            })
          ]);

          const stats = statsResult.status === 'fulfilled' && statsResult.value?.success
            ? statsResult.value.data
            : null;
          const questions = questionsResult.status === 'fulfilled' && questionsResult.value?.success
            ? questionsResult.value.data
            : null;
          const targets = targetsResult.status === 'fulfilled' && targetsResult.value?.success
            ? targetsResult.value.data?.targets || []
            : [];

          const name = availableCampaigns.find((c) => c._id === campaignId)?.name || 'Campaign';

          return { id: campaignId, name, stats, questions, targets };
        })
      );

      const successful = results
        .filter((res) => res.status === 'fulfilled' && res.value?.stats)
        .map((res) => res.value);

      if (!successful.length) {
        setMultiAggregates(null);
        setMultiError('No campaign stats could be loaded.');
        return;
      }

      const overview = buildAggregatedOverview(successful);
      const organizations = buildAggregatedOrganizations(successful);
      const departments = buildAggregatedDepartments(successful);
      const departmentGroups = buildAggregatedDepartmentGroups(departments);
      const questions = buildAggregatedQuestionInsights(successful);

      // Extract unique organizations from aggregated data
      const orgs = Array.from(new Set(
        organizations.map(org => org.organization)
      )).map(name => ({ label: name, value: name }));
      setAvailableOrganizations(orgs);

      setMultiAggregates({
        overview,
        organizations,
        departments,
        departmentGroups,
        questions,
        campaigns: successful.map((item) => ({ id: item.id, name: item.name }))
      });
    } catch (error) {
      console.error('Error aggregating campaigns:', error);
      setMultiError('Failed to load aggregated report');
      setMultiAggregates(null);
    } finally {
      setMultiLoading(false);
    }
  }, [
    availableCampaigns,
    buildAggregatedDepartments,
    buildAggregatedDepartmentGroups,
    buildAggregatedOrganizations,
    buildAggregatedOverview,
    buildAggregatedQuestionInsights
  ]);

  useEffect(() => {
    if (selectedCampaignIds.length) {
      fetchMultiCampaignStats(selectedCampaignIds, selectedOrganizations, multiDateRange);
    }
    if (!selectedCampaignIds.length) {
      setMultiAggregates(null);
    }
  }, [fetchMultiCampaignStats, selectedCampaignIds, selectedOrganizations, multiDateRange]);

  const departmentStats = useMemo(() => (
    Array.isArray(statistics?.byDepartment)
      ? statistics.byDepartment.map((dept) => ({
          ...dept,
          responseRate: coerceRate(dept?.responseRate),
          completionRate: coerceRate(dept?.completionRate),
          responseRateDisplay: getRateDisplay(dept?.responseRate),
          completionRateDisplay: getRateDisplay(dept?.completionRate),
          averageScore: coerceRate(dept?.averageScore),
          averageScoreDisplay: getRateDisplay(dept?.averageScore)
        }))
      : []
  ), [statistics, coerceRate, getRateDisplay]);

  const percentValue = useCallback((value) => {
    const numeric = normalizePercent(value);
    if (!Number.isFinite(numeric)) return 0;
    // Round to 2 decimal places to prevent floating point precision issues
    return Math.round(numeric * 100) / 100;
  }, [normalizePercent]);

  const departmentGroupStats = useMemo(() => {
    if (!departmentStats.length) {
      return [];
    }

    const grouped = new Map();

    departmentStats.forEach((dept) => {
      const resolvedGroup = resolveDepartmentGroup(dept?.department);
      const groupKey = resolvedGroup?.key || 'other';

      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, {
          key: groupKey,
          label: resolvedGroup?.label || 'Other Departments',
          departments: new Set(),
          totalParticipants: 0,
          respondedCount: 0,
          completedCount: 0,
          scoreSum: 0
        });
      }

      const entry = grouped.get(groupKey);
      if (dept?.department) {
        entry.departments.add(dept.department);
      }
      entry.totalParticipants += dept?.totalParticipants || 0;
      entry.respondedCount += dept?.respondedCount || 0;
      entry.completedCount += dept?.completedCount || 0;
      entry.scoreSum += (dept?.averageScore || 0) * (dept?.completedCount || 0);
    });

    return Array.from(grouped.values()).map((group) => {
      const participants = group.totalParticipants || 0;
      const responded = group.respondedCount || 0;
      const completed = group.completedCount || 0;
      const responseRate = participants > 0 ? (responded / participants) * 100 : 0;
      const completionRate = participants > 0 ? (completed / participants) * 100 : 0;
      const averageScore = completed > 0 ? (group.scoreSum / completed) : 0;

      return {
        ...group,
        departments: Array.from(group.departments),
        responseRate,
        completionRate,
        averageScore,
        responseRateDisplay: formatPercentDisplay(responseRate, 2),
        completionRateDisplay: formatPercentDisplay(completionRate, 2),
        averageScoreDisplay: formatPercentDisplay(averageScore, 2)
      };
    }).sort((a, b) => (b.totalParticipants || 0) - (a.totalParticipants || 0));
  }, [departmentStats, formatPercentDisplay, getRateDisplay]);

  const departmentChartSeries = useMemo(() => (
    departmentStats.flatMap((dept) => {
      const label = dept?.department || 'Unassigned';
      const participants = Number.isFinite(dept?.totalParticipants) ? dept.totalParticipants : 0;
      const responded = Number.isFinite(dept?.respondedCount) ? dept.respondedCount : 0;
      const completed = Number.isFinite(dept?.completedCount) ? dept.completedCount : 0;
      const responseValue = percentValue(dept?.responseRate);
      const completionValue = percentValue(dept?.completionRate);
      const averageValue = percentValue(dept?.averageScore);

      return [
        {
          department: label,
          metric: 'Response Rate',
          percentValue: responseValue,
          percentLabel: formatPercentDisplay(responseValue, 2),
          rawValue: responded,
          rawLabel: formatCountDisplay(responded, participants)
        },
        {
          department: label,
          metric: 'Completion Rate',
          percentValue: completionValue,
          percentLabel: formatPercentDisplay(completionValue, 2),
          rawValue: completed,
          rawLabel: formatCountDisplay(completed, participants)
        },
        {
          department: label,
          metric: 'Average Result',
          percentValue: averageValue,
          percentLabel: formatPercentDisplay(averageValue, 2),
          rawValue: averageValue,
          rawLabel: formatNumberDisplay(averageValue, 2)
        }
      ];
    })
  ), [departmentStats, percentValue, formatPercentDisplay, formatCountDisplay, formatNumberDisplay]);

  const departmentChartData = useMemo(() => {
    const usePercent = departmentMetricsMode === 'percent';
    return departmentChartSeries.map((datum) => ({
      ...datum,
      value: usePercent ? datum.percentValue : datum.rawValue,
      displayValue: usePercent ? datum.percentLabel : datum.rawLabel,
      valueMode: usePercent ? 'percent' : 'raw'
    }));
  }, [departmentChartSeries, departmentMetricsMode]);

  const departmentGroupChartSeries = useMemo(() => (
    departmentGroupStats.flatMap((group) => {
      const label = group?.label || 'Other Departments';
      const participants = Number.isFinite(group?.totalParticipants) ? group.totalParticipants : 0;
      const responded = Number.isFinite(group?.respondedCount) ? group.respondedCount : 0;
      const completed = Number.isFinite(group?.completedCount) ? group.completedCount : 0;
      const responseValue = percentValue(group?.responseRate);
      const completionValue = percentValue(group?.completionRate);
      const averageValue = percentValue(group?.averageScore);

      return [
        {
          groupLabel: label,
          metric: 'Response Rate',
          percentValue: responseValue,
          percentLabel: formatPercentDisplay(responseValue, 2),
          rawValue: responded,
          rawLabel: formatCountDisplay(responded, participants)
        },
        {
          groupLabel: label,
          metric: 'Completion Rate',
          percentValue: completionValue,
          percentLabel: formatPercentDisplay(completionValue, 2),
          rawValue: completed,
          rawLabel: formatCountDisplay(completed, participants)
        },
        {
          groupLabel: label,
          metric: 'Average Result',
          percentValue: averageValue,
          percentLabel: formatPercentDisplay(averageValue, 2),
          rawValue: averageValue,
          rawLabel: formatNumberDisplay(averageValue, 2)
        }
      ];
    })
  ), [departmentGroupStats, percentValue, formatPercentDisplay, formatCountDisplay, formatNumberDisplay]);

  const departmentGroupChartData = useMemo(() => {
    const usePercent = departmentMetricsMode === 'percent';
    return departmentGroupChartSeries.map((datum) => ({
      ...datum,
      value: usePercent ? datum.percentValue : datum.rawValue,
      displayValue: usePercent ? datum.percentLabel : datum.rawLabel,
      valueMode: usePercent ? 'percent' : 'raw'
    }));
  }, [departmentGroupChartSeries, departmentMetricsMode]);

  const departmentMetricColors = useMemo(() => ({
    'Response Rate': '#1677ff',
    'Completion Rate': '#34c38f',
    'Average Result': '#faad14'
  }), []);

  const departmentColumnConfig = useMemo(() => {
    const usePercent = departmentMetricsMode === 'percent';
    return {
      data: departmentChartData,
      xField: 'department',
      yField: 'value',
      colorField: 'metric',
      group: true,
      columnWidthRatio: 0.5,
      maxColumnWidth: 28,
      legend: { position: 'top' },
      padding: [16, 16, 32, 32],
      color: ['#1677ff', '#34c38f', '#faad14'],
      label: {
        position: 'top',
        style: {
          fill: '#fff',
          fontSize: 12,
          fontWeight: 600
        }
      },
      yAxis: {
        max: usePercent ? 100 : undefined
      },
      meta: {
        value: { alias: usePercent ? 'Rate (%)' : 'Count' }
      }
    };
  }, [
    departmentChartData,
    departmentMetricsMode
  ]);

  const departmentGroupColumnConfig = useMemo(() => {
    const usePercent = departmentMetricsMode === 'percent';
    return {
      data: departmentGroupChartData,
      xField: 'groupLabel',
      yField: 'value',
      colorField: 'metric',
      group: true,
      columnWidthRatio: 0.5,
      maxColumnWidth: 28,
      legend: { position: 'top' },
      padding: [16, 16, 32, 32],
      color: ['#1677ff', '#34c38f', '#faad14'],
      label: {
        position: 'top',
        style: {
          fill: '#fff',
          fontSize: 12,
          fontWeight: 600
        }
      },
      yAxis: {
        max: usePercent ? 100 : undefined
      },
      meta: {
        value: { alias: usePercent ? 'Rate (%)' : 'Count' }
      }
    };
  }, [
    departmentGroupChartData,
    departmentMetricsMode
  ]);

  const highlightedDepartments = useMemo(() => departmentStats.slice(0, 6), [departmentStats]);
  const highlightedDepartmentGroups = useMemo(() => departmentGroupStats.slice(0, 6), [departmentGroupStats]);
  const hasDepartmentData = departmentStats.length > 0;
  const hasDepartmentGroupData = departmentGroupStats.length > 0;
  const departmentSummary = useMemo(() => {
    if (!hasDepartmentData) return null;
    const totals = departmentStats.reduce((acc, dept) => {
      acc.participants += dept.totalParticipants || 0;
      acc.responded += dept.respondedCount || 0;
      acc.completed += dept.completedCount || 0;
      acc.scoreSum += (dept.averageScore || 0) * (dept.completedCount || 0);
      return acc;
    }, { participants: 0, responded: 0, completed: 0, scoreSum: 0 });

    if (!totals.participants) {
      return { responseRate: 0, completionRate: 0, averageScore: 0 };
    }

    return {
      responseRate: (totals.responded / totals.participants) * 100,
      completionRate: (totals.completed / totals.participants) * 100,
      averageScore: totals.completed > 0
        ? totals.scoreSum / totals.completed
        : 0
    };
  }, [departmentStats, hasDepartmentData]);

  const departmentGroupSummary = useMemo(() => {
    if (!hasDepartmentGroupData) return null;
    const totals = departmentGroupStats.reduce((acc, group) => {
      acc.participants += group.totalParticipants || 0;
      acc.responded += group.respondedCount || 0;
      acc.completed += group.completedCount || 0;
      acc.scoreSum += group.scoreSum || 0;
      return acc;
    }, { participants: 0, responded: 0, completed: 0, scoreSum: 0 });

    if (!totals.participants) {
      return { responseRate: 0, completionRate: 0, averageScore: 0 };
    }

    return {
      responseRate: (totals.responded / totals.participants) * 100,
      completionRate: (totals.completed / totals.participants) * 100,
      averageScore: totals.completed > 0
        ? totals.scoreSum / totals.completed
        : 0
    };
  }, [departmentGroupStats, hasDepartmentGroupData]);

  const createDeliveryTooltip = (total) => ({
    showTitle: false,
    shared: false,
    fields: ['stage', 'value'],
    formatter: (stage, value) => ({
      name: stage || 'Stage',
      value: formatTooltipValue(Number(value) || 0, total)
    }),
    domStyles: {
      'g2-tooltip-list': {
        margin: 0
      },
      'g2-tooltip-list-item': {
        justifyContent: 'space-between',
        minWidth: '140px'
      },
      'g2-tooltip-name': {
        fontSize: '12px',
        color: '#8c8c8c',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      },
      'g2-tooltip-value': {
        fontSize: '16px',
        fontWeight: 600,
        color: '#141414'
      }
    }
  });

  const smsColumnConfig = useMemo(() => {
    const smsTotalCount = smsDeliveryData.reduce((sum, item) => sum + item.value, 0);
    return {
      data: smsDeliveryData,
      xField: 'stage',
      yField: 'value',
      columnWidthRatio: 0.5,
      height: 220,
      color: ['#2E86AB', '#52c41a', '#faad14', '#ff4d4f'],
      label: {
        position: 'inside',
        style: { fill: '#fff', fontSize: 12, fontWeight: 600 }
      }
    };
  }, [smsDeliveryData]);

  const whatsappColumnConfig = useMemo(() => {
    const whatsappTotalCount = whatsappDeliveryData.reduce((sum, item) => sum + item.value, 0);
    return {
      data: whatsappDeliveryData,
      xField: 'stage',
      yField: 'value',
      columnWidthRatio: 0.5,
      height: 220,
      color: ['#2E86AB', '#52c41a', '#faad14', '#ff4d4f'],
      label: {
        position: 'inside',
        style: { fill: '#fff', fontSize: 12, fontWeight: 600 }
      }
    };
  }, [whatsappDeliveryData]);

  const hasSmsActivity = useMemo(() => smsDeliveryData.some(item => item.value > 0), [smsDeliveryData]);
  const hasWhatsappActivity = useMemo(() => whatsappDeliveryData.some(item => item.value > 0), [whatsappDeliveryData]);
  
  // Use backend question analysis data
  const questionDifficulty = questionAnalysis || { mostDifficult: null, easiest: null };

  const hasInvitationBeenSent = useCallback((invitation) => {
    if (!invitation) {
      return false;
    }
    if (invitation.sent) {
      return true;
    }
    const status = (invitation.status || '').toLowerCase();
    return Boolean(status && status !== 'pending');
  }, []);

  const getInvitationStatusLabel = useCallback((invitation) => {
    if (!invitation) {
      return 'PENDING';
    }
    // A WhatsApp read receipt can arrive after the "delivered" event and should take
    // precedence over it, even if a later, unrelated webhook event overwrote providerStatus.
    if (invitation.readAt) {
      return 'READ';
    }
    return (invitation.providerStatus || invitation.status || (invitation.sent ? 'sent' : 'pending') || 'pending').toUpperCase();
  }, []);

  // Renders one entry of an invitation's status timeline (used for both the single
  // current-status case and the full history case below).
  const renderStatusHistoryEntry = useCallback((statusLabel, timestamp, key, description) => (
    <div key={key} style={{ marginBottom: 3, fontSize: 12, lineHeight: 1.5, color: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <span>{statusLabel}</span>
        <span style={{ color: 'rgba(255,255,255,0.75)' }}>
          {timestamp ? new Date(timestamp).toLocaleString() : '—'}
        </span>
      </div>
      {description && (
        <div style={{ color: 'rgba(255,255,255,0.75)', fontStyle: 'italic' }}>
          {description}
        </div>
      )}
    </div>
  ), []);

  // Builds the hover tooltip for a channel tag. WhatsApp invitations carry a
  // statusHistory array (sent -> delivered -> read, etc.) captured from provider
  // webhooks; SMS only ever has its latest status, so we fall back to a single row.
  const getInvitationHistoryTooltip = useCallback((invitation, label) => {
    if (!invitation?.sent && !hasInvitationBeenSent(invitation)) {
      return label;
    }

    const history = Array.isArray(invitation.statusHistory) ? invitation.statusHistory : [];
    const lastError = invitation.lastError || invitation.providerStatusDesc || invitation.errorCode;

    return (
      <div style={{ maxWidth: 280, color: '#fff' }}>
        <div style={{ marginBottom: 4 }}><strong>{label} status</strong></div>
        {history.length > 0 ? (
          history
            .slice()
            .reverse()
            .map((entry, idx) => renderStatusHistoryEntry(
              (entry.providerStatus || entry.status || 'unknown').toUpperCase(),
              entry.timestamp,
              idx,
              entry.description
            ))
        ) : (
          renderStatusHistoryEntry(
            getInvitationStatusLabel(invitation),
            invitation.lastStatusAt || invitation.deliveredAt || invitation.sentAt,
            'current',
            lastError
          )
        )}
      </div>
    );
  }, [getInvitationStatusLabel, hasInvitationBeenSent, renderStatusHistoryEntry]);

  const getInvitationTooltipContent = useCallback((invitation, channelLabel) => {
    if (!invitation || !hasInvitationBeenSent(invitation)) {
      return `Send ${channelLabel} invitation`;
    }

    const statusLabel = getInvitationStatusLabel(invitation);
    const referenceDate = invitation.lastStatusAt || invitation.sentAt || invitation.deliveredAt;
    const lastUpdated = referenceDate ? new Date(referenceDate).toLocaleString() : null;
    const lastError = invitation.lastError || invitation.providerStatusDesc;

    return (
      <div style={{ maxWidth: 260 }}>
        <div><strong>{channelLabel} already sent</strong></div>
        <div>Status: {statusLabel}</div>
        {lastUpdated && <div>Last update: {lastUpdated}</div>}
        {lastError && <div>Last error: {lastError}</div>}
        <div>Click to resend or override recipient.</div>
      </div>
    );
  }, [getInvitationStatusLabel, hasInvitationBeenSent]);


  // Handle actions
  const handleCopyLink = useCallback(async (target) => {
    const link = `${window.location.origin}/campaign-quiz/${target._id}`;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
        message.success('Quiz link copied to clipboard');
      } else {
        // Fallback for environments without clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          message.success('Quiz link copied to clipboard');
        } else {
          throw new Error('Copy command failed');
        }
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      if (window.prompt) {
        window.prompt('Copy this link:', link);
      } else {
        message.error('Unable to copy. Please copy manually: ' + link);
      }
    }
  }, []);
  const handleSendEmail = useCallback((target) => {
    const email = target.participantId?.email;
    const name = target.participantId ? `${target.participantId.name} ${target.participantId.surname}` : 'Participant';
    if (email) {
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(`${campaign?.name || 'Campaign'} quiz invitation`)}&body=${encodeURIComponent(`Hello ${name},

You have been invited to participate in the ${campaign?.name || 'campaign'} quiz.

Please use this link to access the quiz:
${window.location.origin}/campaign-quiz/${target._id}

Thank you!`)}`;
      window.location.href = mailtoLink;
    } else {
      message.error('Email address not available');
    }
  }, [campaign?.name]);

  const openMessagingModal = useCallback((mode, target, options = {}) => {
    setMessageModalMode(mode);
    setMessageModalTarget(target);
    setMessageModalRequiresOverride(Boolean(options.requiresOverride));
    setMessageModalOpen(true);
  }, []);

  const handleSendSMS = useCallback((target) => {
    const invitationSnapshot = target?.smsInvitation || target?.invitations?.sms;
    const requiresOverride = hasInvitationBeenSent(invitationSnapshot);
    openMessagingModal('sms', target, { requiresOverride });
  }, [hasInvitationBeenSent, openMessagingModal]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const total = apiTargetTotal || 5000;
      const resp = await campaignService.getCampaignTargets(id, {
        page: 1,
        limit: total,
        fields: TARGET_FIELD_SELECTION,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(searchText && { search: searchText }),
      });
      const rows = (resp.data?.targets || []).map(simplifyTargetRecord);

      const fmtDate = (v) => v ? new Date(v).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
      const fmtDuration = (t) => {
        if (!t.startedAt || !t.completedAt) return '';
        const ms = new Date(t.completedAt) - new Date(t.startedAt);
        if (ms <= 0) return '';
        const totalSec = Math.round(ms / 1000);
        const m = Math.floor(totalSec / 60), s = totalSec % 60;
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
      };
      const fmtPhone = (t) => {
        const p = t.participantId?.phoneNumber;
        return p?.full || (p?.countryCode && p?.number ? `${p.countryCode}${p.number}` : '') || '';
      };
      const fmtDeliveryChannel = (r) => {
        const sms = r.smsInvitation;
        const wa  = r.whatsappInvitation;
        const smsDelivered = sms?.sent && sms?.status === 'delivered';
        const waDelivered  = wa?.sent  && wa?.status  === 'success';
        if (smsDelivered && waDelivered) return 'SMS + WhatsApp';
        if (smsDelivered) return 'SMS';
        if (waDelivered)  return 'WhatsApp';
        // Not confirmed delivered — show what was attempted with its raw status
        const attempted = [];
        if (sms?.sent) attempted.push(`SMS (${sms.status || 'sent'})`);
        if (wa?.sent)  attempted.push(`WhatsApp (${wa.status || 'sent'})`);
        return attempted.length ? attempted.join(', ') : 'Not sent';
      };

      const fieldGetters = {
        name:            (r) => r.participantId?.name || '',
        surname:         (r) => r.participantId?.surname || '',
        email:           (r) => r.participantId?.email || '',
        phone:           (r) => fmtPhone(r),
        businessUnit:    (r) => r.participantId?.businessUnit?.name || '',
        department:      (r) => r.participantId?.department || '',
        status:          (r) => r.status || '',
        score:           (r) => r.status === 'completed' && typeof r.score === 'number' ? `${r.score}%` : '',
        startedAt:       (r) => fmtDate(r.startedAt),
        completedAt:     (r) => fmtDate(r.completedAt),
        deliveryChannel: (r) => fmtDeliveryChannel(r),
        completionTime:  (r) => fmtDuration(r),
        device:          (r) => r.sessionData?.deviceInfo || '',
      };

      const headers = exportFields.map(k => ALL_EXPORT_FIELDS.find(f => f.key === k)?.label || k);
      const csvRows = [
        headers,
        ...rows.map(r => exportFields.map(k => {
          const val = fieldGetters[k]?.(r) ?? '';
          const str = String(val);
          return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"` : str;
        }))
      ];

      const csv = csvRows.map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = (campaign?.name || 'campaign').replace(/[^a-zA-Z0-9-_\s]/g, '').trim().replace(/\s+/g, '-');
      link.href = url;
      link.download = `${safeName}-targets.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setExportModalOpen(false);
    } catch (err) {
      message.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSendWhatsApp = useCallback((target) => {
    const invitationSnapshot = target?.whatsappInvitation || target?.invitations?.whatsapp;
    const requiresOverride = hasInvitationBeenSent(invitationSnapshot);
    openMessagingModal('whatsapp', target, { requiresOverride });
  }, [hasInvitationBeenSent, openMessagingModal]);

  const handleCloseMessagingModal = useCallback(() => {
    setMessageModalOpen(false);
    setMessageModalTarget(null);
    setMessageModalRequiresOverride(false);
  }, []);

  const handleMessageSent = useCallback(() => {
    fetchTargets(1, searchText, statusFilter);
  }, [fetchTargets, searchText, statusFilter]);

  const handleOpenQuizInNewTab = useCallback((target) => {
    const link = `${window.location.origin}/campaign-quiz/${target._id}`;
    window.open(link, '_blank');
  }, []);

  const handleCloseAnswersModal = () => {
    setAnswersModalVisible(false);
    setParticipantAnswers(null);
    setSelectedTarget(null);
  };

  const handleViewAttempts = useCallback((target) => {
    if (!target?.attempts || target.attempts.length === 0) {
      message.info('No attempt history available for this participant.');
      return;
    }
    setAttemptsModalTarget(target);
    setAttemptsModalVisible(true);
  }, []);

  const handleCloseAttemptsModal = () => {
    setAttemptsModalVisible(false);
    setAttemptsModalTarget(null);
  };

  const formatAttemptDuration = (seconds) => {
    if (!seconds || seconds < 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const handleViewResponses = useCallback(async (target) => {
    if (!target?._id) return;

    if (target.status !== 'completed') {
      message.info('Responses will be available once the participant completes the quiz.');
      return;
    }

    setSelectedTarget(target);
    setAnswersModalVisible(true);
    setAnswersLoading(true);

    try {
      const response = await campaignService.getParticipantAnswers(target._id);
      if (response.success) {
        setParticipantAnswers(response.data);
      } else {
        message.error(response.message || 'Failed to load responses');
        handleCloseAnswersModal();
      }
    } catch (error) {
      console.error('Error loading participant answers:', error);
      message.error('Failed to load responses');
      handleCloseAnswersModal();
    } finally {
      setAnswersLoading(false);
    }
  }, []);

  // Status colors
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'processing';
      case 'pending': return 'default';
      default: return 'default';
    }
  }, []);

  

  const normalizeAnswerSelections = (answer) => {
    if (answer === null || answer === undefined) return [];
    if (Array.isArray(answer)) return answer;
    if (typeof answer === 'object') {
      if (Array.isArray(answer.selectedOptions)) return answer.selectedOptions;
      if (Array.isArray(answer.answers)) return answer.answers;
      if (Array.isArray(answer.values)) return answer.values;
      const candidate = answer.optionId ?? answer.id ?? answer.value ?? answer.key;
      return candidate === undefined || candidate === null ? [] : [candidate];
    }
    return [answer];
  };

  // Handle toggling includeInResults for a campaign target
  const handleToggleIncludeInResults = useCallback(async (targetId, includeInResults) => {
    try {
      const result = await campaignService.toggleTargetIncludeInResults(targetId, includeInResults);
      if (result.success) {
        // Update local state immediately
        setTargets(prev => prev.map(t => 
          t._id === targetId ? { ...t, includeInResults } : t
        ));
        message.success(`Target ${includeInResults ? 'included in' : 'excluded from'} results`);
        // Refresh statistics to reflect the change
        fetchCampaignData();
      } else {
        message.error(result.message || 'Failed to update target');
      }
    } catch (error) {
      message.error('Failed to update target');
    }
  }, [fetchCampaignData]);

  const formatAnswerValue = (answer) => {
    if (answer === null || answer === undefined) {
      return 'No answer recorded';
    }
    if (typeof answer === 'string' || typeof answer === 'number' || typeof answer === 'boolean') {
      return String(answer);
    }
    try {
      return JSON.stringify(answer, null, 2);
    } catch (error) {
      return String(answer);
    }
  };

  // Table columns
  const columns = useMemo(() => ([
    {
      title: 'Organisation',
      key: 'organization',
      render: (_, record) => record?.participantId?.businessUnit?.name || record?.organizationSnapshot || 'N/A',
      sorter: (a, b) => (a?.participantId?.businessUnit?.name || a?.organizationSnapshot || '').localeCompare(b?.participantId?.businessUnit?.name || b?.organizationSnapshot || ''),
    },
    {
      title: 'Department',
      key: 'department',
      render: (_, record) => record?.participantId?.department || record?.departmentSnapshot || 'N/A',
      sorter: (a, b) => (a?.participantId?.department || a?.departmentSnapshot || '').localeCompare(b?.participantId?.department || b?.departmentSnapshot || ''),
    },
    {
      title: 'Participant',
      key: 'participant',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record?.participantId ? `${record.participantId.name || ''} ${record.participantId.surname || ''}`.trim() : 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record?.participantId?.email || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Phone',
      key: 'phone',
      render: (_, record) => {
        const phoneNumber = record?.participantId?.phoneNumber;
        if (phoneNumber?.full) {
          return phoneNumber.full;
        } else if (phoneNumber?.countryCode && phoneNumber?.number) {
          return `${phoneNumber.countryCode}${phoneNumber.number}`;
        }
        return 'N/A';
      },
    },
    {
      title: 'Channel',
      key: 'channel',
      width: 120,
      render: (_, record) => {
        const sms = record?.smsInvitation;
        const wa = record?.whatsappInvitation;
        const smsDelivered = sms?.sent && sms?.status === 'delivered';
        const waDelivered = wa?.sent && wa?.status === 'success';

        const smsTag = sms?.sent ? (
          <Tooltip title={getInvitationHistoryTooltip(sms, 'SMS')}>
            <Tag color="blue" style={{ margin: 0, opacity: smsDelivered ? 1 : 0.6, cursor: 'pointer' }}>SMS</Tag>
          </Tooltip>
        ) : null;

        const waTag = wa?.sent ? (
          <Tooltip title={getInvitationHistoryTooltip(wa, 'WhatsApp')}>
            <Tag color="green" style={{ margin: 0, opacity: waDelivered ? 1 : 0.6, cursor: 'pointer' }}>WhatsApp</Tag>
          </Tooltip>
        ) : null;

        // Show only the channel that actually delivered, unless both delivered
        if (smsDelivered && !waDelivered) return smsTag;
        if (waDelivered && !smsDelivered) return waTag;

        // Both or neither delivered — show all attempted
        if (!smsTag && !waTag) return <Text type="secondary">-</Text>;
        return <Space size={4} wrap>{smsTag}{waTag}</Space>;
      },
      filters: [
        { text: 'SMS', value: 'sms' },
        { text: 'WhatsApp', value: 'whatsapp' },
      ],
      onFilter: (value, record) => {
        if (value === 'sms') return !!record?.smsInvitation?.sent;
        if (value === 'whatsapp') return !!record?.whatsappInvitation?.sent;
        return false;
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Tag color={getStatusColor(record?.status || 'pending')}>
          {(record?.status || 'pending').toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Completed', value: 'completed' },
        { text: 'In Progress', value: 'in-progress' },
        { text: 'Pending', value: 'pending' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Score',
      key: 'score',
      render: (_, record) => record?.status === 'completed' && typeof record?.score === 'number' ? `${record.score}%` : '-',
      sorter: (a, b) => (a?.score || 0) - (b?.score || 0),
    },
    {
      title: 'Completion Time',
      key: 'completionTime',
      render: (_, record) => {
        if (record?.startedAt && record?.completedAt) {
          const durationMs = new Date(record.completedAt) - new Date(record.startedAt);
          if (durationMs > 0) {
            return formatDuration(durationMs, { unit: 'milliseconds' });
          }
        }
        return '-';
      },
      sorter: (a, b) => {
        const aDuration = (a?.startedAt && a?.completedAt) 
          ? (new Date(a.completedAt) - new Date(a.startedAt)) / (1000 * 60) 
          : 0;
        const bDuration = (b?.startedAt && b?.completedAt) 
          ? (new Date(b.completedAt) - new Date(b.startedAt)) / (1000 * 60) 
          : 0;
        return aDuration - bDuration;
      },
    },
    {
      title: 'Completed',
      key: 'completedAt',
      render: (_, record) => {
        if (record?.completedAt) {
          return new Date(record.completedAt).toLocaleString(undefined, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
        return '-';
      },
      sorter: (a, b) => {
        if (!a?.completedAt && !b?.completedAt) return 0;
        if (!a?.completedAt) return 1;
        if (!b?.completedAt) return -1;
        return new Date(a.completedAt) - new Date(b.completedAt);
      },
    },
    {
      title: 'Device',
      key: 'device',
      render: (_, record) => {
        const summary = record?.sessionData?.deviceInfo;
        if (!summary) return <Text type="secondary">-</Text>;
        const isPhone = /iPhone|Android|Mobile/i.test(summary);
        return (
          <Space size={4}>
            {isPhone ? <MobileOutlined /> : <LaptopOutlined />}
            <Text style={{ fontSize: '13px' }}>{summary}</Text>
          </Space>
        );
      },
      filters: targets
        .map(t => t?.sessionData?.deviceInfo)
        .filter((v, i, a) => v && a.indexOf(v) === i)
        .map(d => ({ text: d, value: d })),
      onFilter: (value, record) => record?.sessionData?.deviceInfo === value,
    },
    {
      title: 'IP Address',
      key: 'ipAddress',
      render: (_, record) => {
        const ip = record?.sessionData?.ipAddress;
        if (!ip) return <Text type="secondary">-</Text>;
        return <Text style={{ fontSize: '13px', fontFamily: 'monospace' }}>{ip}</Text>;
      },
      sorter: (a, b) => (a?.sessionData?.ipAddress || '').localeCompare(b?.sessionData?.ipAddress || ''),
      filters: targets
        .map(t => t?.sessionData?.ipAddress)
        .filter((v, i, a) => v && a.indexOf(v) === i)
        .map(ip => ({ text: ip, value: ip })),
      onFilter: (value, record) => record?.sessionData?.ipAddress === value,
    },
    {
      title: 'Include in Results',
      key: 'includeInResults',
      width: 130,
      align: 'center',
      render: (_, record) => (
        <Switch 
          size="small"
          checked={record.includeInResults !== false}
          onChange={(checked) => handleToggleIncludeInResults(record._id, checked)}
        />
      ),
      filters: [
        { text: 'Included', value: true },
        { text: 'Excluded', value: false },
      ],
      onFilter: (value, record) => (record.includeInResults !== false) === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => {
        const hasAttempts = record.attempts && record.attempts.length > 0;
        
        // Build dropdown menu items
        const menuItems = [
          {
            key: 'open',
            icon: <EyeOutlined />,
            label: 'Open Quiz',
            onClick: () => handleOpenQuizInNewTab(record)
          },
          {
            key: 'copy',
            icon: <LinkOutlined />,
            label: 'Copy Link',
            onClick: () => handleCopyLink(record)
          },
          { type: 'divider' },
          {
            key: 'email',
            icon: <MailOutlined />,
            label: 'Send Email',
            onClick: () => handleSendEmail(record)
          },
          {
            key: 'sms',
            icon: <PhoneOutlined />,
            label: 'Send SMS',
            onClick: () => handleSendSMS(record)
          },
          {
            key: 'whatsapp',
            icon: <WhatsAppOutlined style={{ color: '#25D366' }} />,
            label: 'Send WhatsApp',
            onClick: () => handleSendWhatsApp(record)
          }
        ];
        
        // Add attempts history if available
        if (hasAttempts) {
          menuItems.splice(2, 0, {
            key: 'attempts',
            icon: <HistoryOutlined />,
            label: `View ${record.attempts.length} Attempt${record.attempts.length > 1 ? 's' : ''}`,
            onClick: () => handleViewAttempts(record)
          });
        }
        
        return (
          <Space size="small">
            <Tooltip title={record.status === 'completed' ? 'View Responses' : 'Responses available after completion'}>
              <Button
                size="small"
                icon={<QuestionCircleOutlined />}
                onClick={() => handleViewResponses(record)}
                disabled={record.status !== 'completed'}
              />
            </Tooltip>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button size="small" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ]), [
    getStatusColor,
    handleCopyLink,
    handleOpenQuizInNewTab,
    handleSendEmail,
    handleSendSMS,
    handleSendWhatsApp,
    handleViewResponses,
    handleViewAttempts,
    handleToggleIncludeInResults
  ]);

  // Chart data
  const responseRate = useMemo(() => {
    if (!stats || !stats.totalTargets) {
      return 0;
    }
    const rate = ((stats.completed + stats.inProgress) / stats.totalTargets) * 100;
    return Number.isFinite(rate) ? rate : 0;
  }, [stats]);

  const responseRateColor = responseRate >= 50 ? '#52c41a' : '#ff4d4f';

  const pieData = useMemo(() => ([
    { type: 'Completed', value: stats.completed },
    { type: 'In Progress', value: stats.inProgress },
    { type: 'Pending', value: stats.pending },
  ].filter(item => item.value > 0)), [stats.completed, stats.inProgress, stats.pending]);

  const pieConfig = useMemo(() => ({
    data: pieData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    legend: {
      position: 'bottom',
    },
    statistic: {
      title: false,
      content: {
        style: {
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        content: `Total\n${stats.totalTargets}`,
      },
    },
  }), [pieData, stats.totalTargets]);

  const selectedCampaignOptions = useMemo(() => {
    if (!Array.isArray(availableCampaigns)) return [];
    return availableCampaigns.map((c) => {
      // Determine status badge color and text
      let statusColor = 'default';
      let statusText = c.status || 'Unknown';
      
      if (c.status === 'completed') {
        statusColor = 'success';
        statusText = 'Completed';
      } else if (c.status === 'pending' || c.status === 'draft') {
        statusColor = 'warning';
        statusText = 'Pending';
      } else if (c.status === 'active' || c.status === 'launched') {
        statusColor = 'processing';
        statusText = 'Active';
      }
      
      return {
        label: c.name,
        value: c._id,
        status: statusText,
        statusColor: statusColor
      };
    });
  }, [availableCampaigns]);

  const renderMultiReport = () => {
    const overview = multiAggregates?.overview || {};
    const organizations = multiAggregates?.organizations || [];
    const departments = multiAggregates?.departments || [];
    const departmentGroups = multiAggregates?.departmentGroups || [];
    const questions = multiAggregates?.questions || {};

    const orgColumns = [
      { title: 'Organisation', dataIndex: 'organization', key: 'organization' },
      { title: 'Participants', dataIndex: 'participants', key: 'participants', render: (v) => formatNumberDisplay(v) },
      { title: 'Completed', dataIndex: 'completed', key: 'completed', render: (v) => formatNumberDisplay(v) },
      { 
        title: 'Completion', 
        dataIndex: 'completionRate', 
        key: 'completionRate',
        render: (v) => formatPercentDisplay(v, 2)
      },
      { 
        title: 'Avg Score', 
        dataIndex: 'averageScore', 
        key: 'averageScore',
        render: (v) => formatPercentDisplay(v, 2)
      }
    ];

    const deptColumns = [
      { title: 'Department', dataIndex: 'department', key: 'department' },
      { title: 'Participants', dataIndex: 'totalParticipants', key: 'totalParticipants', render: (v) => formatNumberDisplay(v) },
      { title: 'Responded', dataIndex: 'respondedCount', key: 'respondedCount', render: (v) => formatNumberDisplay(v) },
      { title: 'Completed', dataIndex: 'completedCount', key: 'completedCount', render: (v) => formatNumberDisplay(v) },
      { title: 'Completion', dataIndex: 'completionRateDisplay', key: 'completionRateDisplay' },
      { title: 'Avg Score', dataIndex: 'averageScoreDisplay', key: 'averageScoreDisplay' }
    ];

    const groupColumns = [
      { title: 'Group', dataIndex: 'label', key: 'label' },
      { title: 'Departments', dataIndex: 'departments', key: 'departments', render: (list = []) => list.join(', ') || '—' },
      { title: 'Participants', dataIndex: 'totalParticipants', key: 'totalParticipants', render: (v) => formatNumberDisplay(v) },
      { title: 'Completed', dataIndex: 'completedCount', key: 'completedCount', render: (v) => formatNumberDisplay(v) },
      { title: 'Completion', dataIndex: 'completionRateDisplay', key: 'completionRateDisplay' },
      { title: 'Avg Score', dataIndex: 'averageScoreDisplay', key: 'averageScoreDisplay' }
    ];

    // Completion status pie chart data
    const multiStatusPieData = [
      { type: 'Completed', value: overview.completed || 0 },
      { type: 'In Progress', value: overview.inProgress || 0 },
      { type: 'Pending', value: overview.pending || 0 }
    ].filter(item => item.value > 0);

    const multiStatusPieConfig = {
      data: multiStatusPieData,
      angleField: 'value',
      colorField: 'type',
      radius: 0.8,
      legend: { position: 'bottom' },
      statistic: {
        title: false,
        content: {
          style: { whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis' },
          content: `Total\n${overview.totalTargets || 0}`
        }
      }
    };

    // Organization performance chart
    const orgChartData = organizations.slice(0, 10).flatMap((org) => [
      {
        organization: org.organization,
        metric: 'Completion Rate',
        value: percentValue(org.completionRate),
        text: org.completionRateDisplay
      },
      {
        organization: org.organization,
        metric: 'Average Score',
        value: percentValue(org.averageScore),
        text: org.averageScoreDisplay
      }
    ]);

    const orgChartConfig = {
      data: orgChartData,
      xField: 'organization',
      yField: 'value',
      colorField: 'metric',
      group: true,
      columnWidthRatio: 0.5,
      maxColumnWidth: 28,
      legend: { position: 'top' },
      padding: [16, 16, 32, 32],
      color: ['#34c38f', '#faad14'],
      label: {
        position: 'top',
        style: { fill: '#fff', fontSize: 12, fontWeight: 600 },
        fields: ['text']
      },
      yAxis: {
        max: 100
      }
    };

    // Department groups chart
    const groupChartData = departmentGroups.flatMap((group) => [
      {
        groupLabel: group.label,
        metric: 'Response Rate',
        value: percentValue(group.responseRate),
        text: group.responseRateDisplay
      },
      {
        groupLabel: group.label,
        metric: 'Completion Rate',
        value: percentValue(group.completionRate),
        text: group.completionRateDisplay
      },
      {
        groupLabel: group.label,
        metric: 'Average Score',
        value: percentValue(group.averageScore),
        text: group.averageScoreDisplay
      }
    ]);

    const groupChartConfig = {
      data: groupChartData,
      xField: 'groupLabel',
      yField: 'value',
      colorField: 'metric',
      group: true,
      columnWidthRatio: 0.5,
      maxColumnWidth: 28,
      legend: { position: 'top' },
      padding: [16, 16, 32, 32],
      color: ['#1677ff', '#34c38f', '#faad14'],
      label: {
        position: 'top',
        style: { fill: '#fff', fontSize: 12, fontWeight: 600 },
        fields: ['text']
      },
      yAxis: {
        max: 100
      }
    };

    // Calculate combined delivery rate across all channels
    const totalSent = (overview.sms?.sent || 0) + (overview.whatsapp?.sent || 0);
    const totalDelivered = (overview.sms?.delivered || 0) + (overview.whatsapp?.success || 0);
    const combinedDeliveryRate = totalSent > 0 
      ? `${((totalDelivered / totalSent) * 100).toFixed(1)}%` 
      : '0%';

    // Top departments chart (top 10)
    const topDepts = departments.slice(0, 10);
    const deptChartData = topDepts.flatMap((dept) => [
      {
        department: dept.department,
        metric: 'Response Rate',
        value: percentValue(dept.responseRate),
        text: dept.responseRateDisplay
      },
      {
        department: dept.department,
        metric: 'Completion Rate',
        value: percentValue(dept.completionRate),
        text: dept.completionRateDisplay
      },
      {
        department: dept.department,
        metric: 'Average Score',
        value: percentValue(dept.averageScore),
        text: dept.averageScoreDisplay
      }
    ]);

    const deptChartConfig = {
      data: deptChartData,
      xField: 'department',
      yField: 'value',
      colorField: 'metric',
      group: true,
      columnWidthRatio: 0.5,
      maxColumnWidth: 28,
      legend: { position: 'top' },
      padding: [16, 16, 32, 48],
      color: ['#1677ff', '#34c38f', '#faad14'],
      label: {
        position: 'top',
        style: { fill: '#fff', fontSize: 11, fontWeight: 600 },
        fields: ['text']
      },
      yAxis: {
        max: 100
      }
    };

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          {multiLoading ? (
            <div style={{ textAlign: 'center', padding: '24px' }}><Spin /></div>
          ) : multiAggregates ? (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Statistic 
                    title="Unique Employees" 
                    value={overview.uniqueEmployees || 0} 
                    prefix={<TeamOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic title="Average Score" value={formatNumberDisplay(overview.averageScore, 1)} suffix="%" prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} />
                </Col>
                <Col span={6}>
                  <Statistic title="Completion Rate" value={formatNumberDisplay(overview.completionRate, 1)} suffix="%" prefix={<TrophyOutlined style={{ color: '#52c41a' }} />} />
                </Col>
                <Col span={6}>
                  <Statistic 
                    title="Quiz Delivery Rate" 
                    value={combinedDeliveryRate} 
                    prefix={<SendOutlined />}
                  />
                </Col>
              </Row>

              {/* Messaging breakdown */}
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={6}>
                  <Card size="small" style={{ background: '#f0f5ff' }}>
                    <Statistic 
                      title="Total Campaign Targets" 
                      value={overview.totalTargets || 0}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ background: '#f9fbff' }}>
                    <Statistic 
                      title="SMS Sent" 
                      value={overview.sms?.sent || 0} 
                      suffix={`/ ${overview.sms?.delivered || 0} delivered`}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ background: '#f6ffed' }}>
                    <Statistic 
                      title="WhatsApp Sent" 
                      value={overview.whatsapp?.sent || 0} 
                      suffix={`/ ${overview.whatsapp?.success || 0} delivered`}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ background: '#fff7e6' }}>
                    <Statistic 
                      title="Total Invitations" 
                      value={totalSent} 
                      suffix={`/ ${totalDelivered} delivered`}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Status Distribution Pie Chart */}
              {multiStatusPieData.length > 0 && (
                <Card title="Status Distribution" size="small">
                  <div style={{ height: 300 }}>
                    <Pie {...multiStatusPieConfig} />
                  </div>
                </Card>
              )}

              {/* Organisation Performance */}
              {organizations.length > 0 && (
                <>
                  <Card title="Performance by Organisation" size="small">
                    <div style={{ height: 320, marginBottom: 16 }}>
                      <Column {...orgChartConfig} />
                    </div>
                    <Table
                      size="small"
                      columns={orgColumns}
                      dataSource={organizations}
                      rowKey={(row) => row.organization}
                      pagination={false}
                      locale={{ emptyText: 'No organisation data' }}
                    />
                  </Card>
                </>
              )}

              {/* Department Groups */}
              {departmentGroups.length > 0 && (
                <Card title="Performance by Groups of Departments" size="small">
                  <div style={{ height: 320, marginBottom: 16 }}>
                    <Column {...groupChartConfig} />
                  </div>
                  <Table
                    size="small"
                    columns={groupColumns}
                    dataSource={departmentGroups}
                    rowKey={(row) => row.key}
                    pagination={false}
                    locale={{ emptyText: 'No grouped department data' }}
                  />
                </Card>
              )}

              {/* Top Departments */}
              {departments.length > 0 && (
                <Card title="Performance by Department" size="small" extra={<Text type="secondary">Top 10 shown in chart</Text>}>
                  <div style={{ height: 360, marginBottom: 16 }}>
                    <Column {...deptChartConfig} />
                  </div>
                  <Table
                    size="small"
                    columns={deptColumns}
                    dataSource={departments}
                    rowKey={(row) => row.department}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: 'No department data' }}
                  />
                </Card>
              )}

              {/* Question Analysis */}
              <Card title="Top 10 Most Difficult Questions" size="small">
                {questions.top10Difficult && questions.top10Difficult.length > 0 ? (
                  <Table
                    size="small"
                    dataSource={questions.top10Difficult}
                    rowKey={(row, index) => `${row.campaign}-${index}`}
                    pagination={false}
                    columns={[
                      {
                        title: '#',
                        key: 'rank',
                        width: 50,
                        render: (_, __, index) => (
                          <Tag color={index === 0 ? 'red' : index < 3 ? 'orange' : 'default'}>
                            {index + 1}
                          </Tag>
                        )
                      },
                      {
                        title: 'Question',
                        dataIndex: 'questionText',
                        key: 'questionText',
                        ellipsis: true
                      },
                      {
                        title: 'Campaign',
                        dataIndex: 'campaign',
                        key: 'campaign',
                        width: 200,
                        ellipsis: true
                      },
                      {
                        title: 'Success Rate',
                        dataIndex: 'successRate',
                        key: 'successRate',
                        width: 120,
                        render: (rate) => (
                          <span style={{ 
                            color: rate < 30 ? '#ff4d4f' : rate < 60 ? '#faad14' : '#52c41a',
                            fontWeight: 600
                          }}>
                            {formatPercentDisplay(rate, 2)}
                          </span>
                        )
                      },
                      {
                        title: 'Failure Rate',
                        dataIndex: 'failureRate',
                        key: 'failureRate',
                        width: 120,
                        render: (rate) => (
                          <span style={{ 
                            color: rate > 70 ? '#ff4d4f' : rate > 40 ? '#faad14' : '#52c41a',
                            fontWeight: 600
                          }}>
                            {formatPercentDisplay(rate, 2)}
                          </span>
                        )
                      },
                      {
                        title: 'Correct/Total',
                        key: 'answers',
                        width: 120,
                        render: (_, record) => `${record.correctAnswers}/${record.totalAnswers}`
                      }
                    ]}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '24px' }}>
                    <Text type="secondary">No question data available yet.</Text>
                  </div>
                )}
              </Card>
            </Space>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              {multiError ? <Text type="danger">{multiError}</Text> : <Text type="secondary">Select one or more campaigns to see the full report.</Text>}
            </div>
          )}
        </Card>
      </Space>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <Text>Campaign not found</Text>
        </Card>
      </div>
    );
  }

  const getCollapseItems = (panelStyle) => [
    {
      key: '1',
      label: (
        <span>
          <TrophyOutlined style={{ marginRight: '8px' }} />
          Campaign Performance Overview
        </span>
      ),
      children: (
        <Card>
          <Row gutter={[24, 24]}>
            <Col span={6}>
              <Statistic
                title="Total Targets"
                value={stats.totalTargets}
                prefix={<TeamOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Completed"
                value={stats.completed}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Not Completed"
                value={stats.totalTargets - stats.completed}
                prefix={<ClockCircleOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Completion Rate"
                value={stats.completionRate}
                suffix="%"
                prefix={<TrophyOutlined style={{ color: stats.completionRate >= 50 ? '#52c41a' : '#ff4d4f' }} />}
                valueStyle={{ color: stats.completionRate >= 50 ? '#52c41a' : '#ff4d4f' }}
              />
            </Col>
          </Row>
          <Divider />
          <Row gutter={[24, 24]}>
            <Col span={6}>
              <Statistic
                title="Average Score"
                value={stats.averageScore}
                suffix="%"
                prefix={<CheckCircleOutlined style={{ color: stats.averageScore >= 70 ? '#52c41a' : '#ff4d4f' }} />}
                valueStyle={{ color: stats.averageScore >= 70 ? '#52c41a' : '#ff4d4f' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Avg. Completion Time"
                value={stats.averageCompletionTime}
                prefix={<ClockCircleOutlined />}
                formatter={(value) => formatDuration(value, { unit: 'minutes' })}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Response Rate"
                value={(responseRate || 0).toFixed(1)}
                suffix="%"
                prefix={<UserOutlined style={{ color: responseRateColor }} />}
                valueStyle={{ color: responseRateColor }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="In Progress"
                value={stats.inProgress}
                prefix={<SendOutlined />}
              />
            </Col>
          </Row>
          <Divider plain>Messaging Delivery</Divider>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <div style={{ background: '#f9fbff', borderRadius: 12, padding: 16, height: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <Text strong>SMS Delivery</Text>
                  <Tag color="blue">Delivery Rate {messagingStats.sms.deliveryRate}</Tag>
                </div>
                {hasSmsActivity ? (
                  <Column {...smsColumnConfig} />
                ) : (
                  <Text type="secondary">No SMS invitations sent yet.</Text>
                )}
              </div>
            </Col>
            <Col span={12}>
              <div style={{ background: '#f6ffed', borderRadius: 12, padding: 16, height: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <Text strong>WhatsApp Delivery</Text>
                  <Tag color="green">Success Rate {messagingStats.whatsapp.successRate}</Tag>
                </div>
                {hasWhatsappActivity ? (
                  <Column {...whatsappColumnConfig} />
                ) : (
                  <Text type="secondary">No WhatsApp invitations sent yet.</Text>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      ),
      style: panelStyle,
    },
    {
      key: '2',
      label: (
        <span>
          <EyeOutlined style={{ marginRight: '8px' }} />
          Visual Analytics & Campaign Details
        </span>
      ),
      children: (
        <Row gutter={[24, 24]} style={{ marginBottom: '16px' }}>
          {/* Status Distribution */}
          <Col span={12}>
            <Card title="Status Distribution" style={{ height: '400px' }} bodyStyle={{ height: 'calc(100% - 56px)' }}>
              <div style={{ height: '100%' }}>
                {pieData.length > 0 ? (
                  <Pie {...pieConfig} />
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    color: '#999'
                  }}>
                    No data available
                  </div>
                )}
              </div>
            </Card>
          </Col>

          {/* Campaign Information */}
          <Col span={12}>
            <Card title="Campaign Information" style={{ height: '400px' }} bodyStyle={{ height: 'calc(100% - 56px)', padding: '24px' }}>
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Row gutter={[24, 24]}>
                  <Col span={12}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>
                          QUIZ
                        </Text>
                        <Text strong style={{ fontSize: '15px', display: 'block', color: '#2E86AB' }}>
                          {campaign?.questionnaire?.title || 'N/A'}
                        </Text>
                      </div>

                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>
                          START DATE
                        </Text>
                        <Text strong style={{ fontSize: '15px', display: 'block' }}>
                          {campaign?.startDate ? new Date(campaign.startDate).toLocaleDateString() : 'N/A'}
                        </Text>
                      </div>

                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>
                          END DATE {canEditEndDate && !isEditingEndDate && (
                            <Tooltip title="Edit end date">
                              <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => {
                                  setNewEndDate(campaign?.endDate ? dayjs(campaign.endDate) : null);
                                  setIsEditingEndDate(true);
                                }}
                                style={{ padding: 0, height: 'auto', marginLeft: 4 }}
                              />
                            </Tooltip>
                          )}
                        </Text>
                        {isEditingEndDate ? (
                          <Space size="small" style={{ marginTop: 4 }}>
                            <DatePicker
                              showTime={{ format: 'HH:mm' }}
                              value={newEndDate}
                              onChange={setNewEndDate}
                              format="YYYY-MM-DD HH:mm"
                              size="small"
                              style={{ width: 170 }}
                              disabledDate={(current) => current && current < dayjs().startOf('day')}
                              placeholder="Select date & time"
                            />
                            <Button
                              type="primary"
                              size="small"
                              icon={<SaveOutlined />}
                              onClick={handleUpdateEndDate}
                              loading={updatingEndDate}
                              disabled={!newEndDate}
                            />
                            <Button
                              size="small"
                              icon={<CloseCircleFilled />}
                              onClick={() => {
                                setIsEditingEndDate(false);
                                setNewEndDate(null);
                              }}
                            />
                          </Space>
                        ) : (
                          <Text strong style={{ fontSize: '15px', display: 'block' }}>
                            {campaign?.endDate ? new Date(campaign.endDate).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A'}
                          </Text>
                        )}
                      </div>
                    </Space>
                  </Col>

                  <Col span={12}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>
                          CREATED BY
                        </Text>
                        <Text strong style={{ fontSize: '15px', display: 'block' }}>
                          {campaign?.createdBy?.name || campaign?.createdBy?.email || 'N/A'}
                        </Text>
                      </div>

                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>
                          CREATED DATE
                        </Text>
                        <Text strong style={{ fontSize: '15px', display: 'block' }}>
                          {campaign?.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : 'N/A'}
                        </Text>
                      </div>

                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>
                          STATUS
                        </Text>
                        <Tag color={
                          campaign?.status === 'active' ? 'green' :
                          campaign?.status === 'completed' ? 'blue' :
                          campaign?.status === 'paused' ? 'orange' :
                          campaign?.status === 'cancelled' ? 'red' : 'default'
                        } style={{ fontSize: '13px', padding: '2px 10px' }}>
                          {(campaign?.status || 'draft').charAt(0).toUpperCase() + (campaign?.status || 'draft').slice(1)}
                        </Tag>
                      </div>
                    </Space>
                  </Col>
                </Row>

                {/* Leaderboard Setting - Full Width */}
                <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px', textTransform: 'uppercase' }}>
                    LEADERBOARD {!isEditingLeaderboard && (
                      <Tooltip title="Edit leaderboard display">
                        <Button
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setNewLeaderboardType(campaign?.settings?.leaderboardType || 'none');
                            setIsEditingLeaderboard(true);
                          }}
                          style={{ padding: 0, height: 'auto', marginLeft: 4 }}
                        />
                      </Tooltip>
                    )}
                  </Text>
                  {isEditingLeaderboard ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
                      <Radio.Group
                        value={newLeaderboardType}
                        onChange={(e) => setNewLeaderboardType(e.target.value)}
                        size="small"
                      >
                        <Space size={16}>
                          <Radio value="none">Don't show</Radio>
                          <Radio value="presentation">Percentage average</Radio>
                          <Radio value="points-percentage">Points & percentage</Radio>
                          <Radio value="campaign-breakdown">Per campaign</Radio>
                          <Radio value="latest-campaign">Latest campaign only</Radio>
                        </Space>
                      </Radio.Group>
                      <Space size="small">
                        <Button
                          type="primary"
                          size="small"
                          icon={<SaveOutlined />}
                          onClick={handleUpdateLeaderboard}
                          loading={updatingLeaderboard}
                        />
                        <Button
                          size="small"
                          icon={<CloseCircleFilled />}
                          onClick={() => setIsEditingLeaderboard(false)}
                        />
                      </Space>
                    </div>
                  ) : (
                    <Tag 
                      style={{ 
                        fontSize: '13px', 
                        padding: '2px 10px',
                        backgroundColor: campaign?.settings?.leaderboardType && campaign?.settings?.leaderboardType !== 'none' 
                          ? `${token.colorPrimary}15` 
                          : undefined,
                        borderColor: campaign?.settings?.leaderboardType && campaign?.settings?.leaderboardType !== 'none' 
                          ? token.colorPrimary 
                          : undefined,
                        color: campaign?.settings?.leaderboardType && campaign?.settings?.leaderboardType !== 'none' 
                          ? token.colorPrimary 
                          : undefined
                      }}
                    >
                      {campaign?.settings?.leaderboardType === 'presentation' ? 'Percentage average' :
                       campaign?.settings?.leaderboardType === 'points-percentage' ? 'Points & percentage' :
                       campaign?.settings?.leaderboardType === 'campaign-breakdown' ? 'Per campaign' :
                       campaign?.settings?.leaderboardType === 'latest-campaign' ? 'Latest campaign only' : 'Disabled'}
                    </Tag>
                  )}
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      ),
      style: panelStyle,
    },
    {
      key: '3',
      label: (
        <span>
          <ApartmentOutlined style={{ marginRight: '8px' }} />
          Department Engagement
        </span>
      ),
      children: (
        <>
          <Row gutter={[24, 24]}>
            <Col span={14}>
              <Card
                title="Response vs Completion Rate"
                style={{ height: '100%' }}
                extra={(
                  <Space size={8} wrap align="center">
                    {departmentSummary && (
                      <Space size={8} wrap>
                        <Tag color="blue">Response {formatPercentDisplay(departmentSummary.responseRate, 1)}</Tag>
                        <Tag color="green">Completion {formatPercentDisplay(departmentSummary.completionRate, 1)}</Tag>
                        <Tag color="gold">Avg Result {formatPercentDisplay(departmentSummary.averageScore, 1)}</Tag>
                      </Space>
                    )}
                    <Segmented
                      size="small"
                      value={departmentMetricsMode}
                      onChange={(value) => setDepartmentMetricsMode(value === 'raw' ? 'raw' : 'percent')}
                      options={[
                        { label: 'Percent (%)', value: 'percent' },
                        { label: 'Raw (#)', value: 'raw' }
                      ]}
                    />
                  </Space>
                )}
              >
                {hasDepartmentData ? (
                  <div style={{ minHeight: '320px' }}>
                    <Column {...departmentColumnConfig} />
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 12px', 
                    color: '#999'
                  }}>
                    <Text type="secondary">Once participants begin responding, departmental trends will show up here.</Text>
                  </div>
                )}
              </Card>
            </Col>
            <Col span={10}>
              <Card title="Department Snapshot" style={{ height: '100%' }}>
                {highlightedDepartments.length > 0 ? (
                  <List
                    size="small"
                    dataSource={highlightedDepartments}
                    split
                    renderItem={(dept) => (
                      <List.Item key={dept.department || 'unassigned'} style={{ alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <Text strong>{dept.department || 'Unassigned'}</Text>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {(dept.totalParticipants || 0)} participants • {(dept.respondedCount || 0)} responded
                          </div>
                        </div>
                        <Space direction="vertical" size={0} align="end">
                          <div>
                            <Text type="secondary" style={{ fontSize: '11px' }}>Response</Text>
                            <div style={{ fontSize: '16px', fontWeight: 600 }}>
                              {dept.responseRateDisplay || formatPercentDisplay(dept.responseRate, 1)}
                            </div>
                          </div>
                          <div>
                            <Text type="secondary" style={{ fontSize: '11px' }}>Completion</Text>
                            <div style={{ fontSize: '16px', fontWeight: 600 }}>
                              {dept.completionRateDisplay || formatPercentDisplay(dept.completionRate, 1)}
                            </div>
                          </div>
                          <div>
                            <Text type="secondary" style={{ fontSize: '11px' }}>Avg Result</Text>
                            <div style={{ fontSize: '16px', fontWeight: 600 }}>
                              {dept.averageScoreDisplay || formatPercentDisplay(dept.averageScore, 1)}
                            </div>
                          </div>
                        </Space>
                      </List.Item>
                    )}
                    locale={{ emptyText: 'No department data yet' }}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 12px', color: '#999' }}>
                    <Text type="secondary">Invitees with department info will appear here as soon as data is available.</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
          
          <Divider style={{ margin: '32px 0 24px' }}>
            <Text type="secondary">Aggregated Department Groups</Text>
          </Divider>
          <Row gutter={[24, 24]}>
            <Col span={14}>
              <Card
                title="Grouped Response vs Completion Rate"
                style={{ height: '100%' }}
                extra={(
                  <Space size={8} wrap align="center">
                    {departmentGroupSummary && (
                      <Space size={8} wrap>
                        <Tag color="blue">Response {formatPercentDisplay(departmentGroupSummary.responseRate, 2)}</Tag>
                        <Tag color="green">Completion {formatPercentDisplay(departmentGroupSummary.completionRate, 2)}</Tag>
                        <Tag color="gold">Avg Result {formatPercentDisplay(departmentGroupSummary.averageScore, 2)}</Tag>
                      </Space>
                    )}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      View: {departmentMetricsMode === 'percent' ? 'Percentages' : 'Raw numbers'}
                    </Text>
                  </Space>
                )}
              >
                {hasDepartmentGroupData ? (
                  <div style={{ minHeight: '320px' }}>
                    <Column {...departmentGroupColumnConfig} />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 12px', color: '#999' }}>
                    <Text type="secondary">Group summaries will display once department data is available.</Text>
                  </div>
                )}
              </Card>
            </Col>
            <Col span={10}>
              <Card title="Group Snapshot" style={{ height: '100%' }}>
                {highlightedDepartmentGroups.length > 0 ? (
                  <List
                    size="small"
                    dataSource={highlightedDepartmentGroups}
                    split
                    renderItem={(group) => (
                      <List.Item key={group.key} style={{ alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <Text strong>{group.label}</Text>
                          {group.departments.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              Includes: {group.departments.join(', ')}
                            </div>
                          )}
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {(group.totalParticipants || 0)} participants • {(group.respondedCount || 0)} responded
                          </div>
                        </div>
                        <Space direction="vertical" size={0} align="end">
                          <div>
                            <Text type="secondary" style={{ fontSize: '11px' }}>Response</Text>
                            <div style={{ fontSize: '16px', fontWeight: 600 }}>
                              {group.responseRateDisplay || formatPercentDisplay(group.responseRate, 2)}
                            </div>
                          </div>
                          <div>
                            <Text type="secondary" style={{ fontSize: '11px' }}>Completion</Text>
                            <div style={{ fontSize: '16px', fontWeight: 600 }}>
                              {group.completionRateDisplay || formatPercentDisplay(group.completionRate, 2)}
                            </div>
                          </div>
                          <div>
                            <Text type="secondary" style={{ fontSize: '11px' }}>Avg Result</Text>
                            <div style={{ fontSize: '16px', fontWeight: 600 }}>
                              {group.averageScoreDisplay || formatPercentDisplay(group.averageScore, 2)}
                            </div>
                          </div>
                        </Space>
                      </List.Item>
                    )}
                    locale={{ emptyText: 'No grouped data yet' }}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 12px', color: '#999' }}>
                    <Text type="secondary">Grouped views will appear as soon as department data is captured.</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </>
      ),
      style: panelStyle,
    },
    {
      key: '4',
      label: (
        <span>
          <QuestionCircleOutlined style={{ marginRight: '8px' }} />
          Question Analysis
        </span>
      ),
      children: (
        <>
          {(questionDifficulty.mostDifficult || questionDifficulty.easiest) ? (
            <Row gutter={[24, 16]}>
              {questionDifficulty.mostDifficult && (
                <Col span={12}>
                  <Card 
                    size="small" 
                    title={
                      <span style={{ color: '#fa8c16' }}>
                        <TrophyOutlined style={{ marginRight: '8px' }} />
                        Most Difficult Question
                      </span>
                    }
                    headStyle={{ backgroundColor: '#fff2e8', borderBottom: '1px solid #ffbb96' }}
                    bodyStyle={{ backgroundColor: '#fafafa', minHeight: '120px', padding: '12px' }}
                    style={{ minHeight: '176px' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div>
                        <Text strong style={{ fontSize: '13px', display: 'block', lineHeight: '1.4' }}>
                          {questionDifficulty.mostDifficult.questionText}
                        </Text>
                      </div>
                      <Row gutter={[12, 6]}>
                        <Col span={12}>
                          <Statistic
                            title="Failure Rate"
                            value={Math.round(questionDifficulty.mostDifficult.failureRate)}
                            suffix="%"
                            valueStyle={{ color: '#fa8c16', fontSize: '16px' }}
                          />
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="Correct Answers"
                            value={`${questionDifficulty.mostDifficult.correctAnswers}/${questionDifficulty.mostDifficult.totalAnswers}`}
                            valueStyle={{ color: '#666', fontSize: '13px' }}
                          />
                        </Col>
                      </Row>
                      {questionDifficulty.mostDifficult.note && (
                        <div style={{ 
                          padding: '6px', 
                          backgroundColor: '#fff2e8', 
                          borderRadius: '4px',
                          fontStyle: 'italic',
                          fontSize: '11px'
                        }}>
                          {questionDifficulty.mostDifficult.note}
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              )}
              {questionDifficulty.easiest && (
                <Col span={12}>
                  <Card 
                    size="small" 
                    title={
                      <span style={{ color: '#52c41a' }}>
                        <CheckCircleOutlined style={{ marginRight: '8px' }} />
                        Easiest Question
                      </span>
                    }
                    headStyle={{ backgroundColor: '#f6ffed', borderBottom: '1px solid #b7eb8f' }}
                    bodyStyle={{ backgroundColor: '#fafafa', minHeight: '120px', padding: '12px' }}
                    style={{ minHeight: '176px' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div>
                        <Text strong style={{ fontSize: '13px', display: 'block', lineHeight: '1.4' }}>
                          {questionDifficulty.easiest.questionText}
                        </Text>
                      </div>
                      <Row gutter={[12, 6]}>
                        <Col span={12}>
                          <Statistic
                            title="Success Rate"
                            value={Math.round(questionDifficulty.easiest.successRate)}
                            suffix="%"
                            valueStyle={{ color: '#52c41a', fontSize: '16px' }}
                          />
                        </Col>
                        <Col span={12}>
                          <Statistic
                            title="Correct Answers"
                            value={`${questionDifficulty.easiest.correctAnswers}/${questionDifficulty.easiest.totalAnswers}`}
                            valueStyle={{ color: '#666', fontSize: '13px' }}
                          />
                        </Col>
                      </Row>
                      {questionDifficulty.easiest.note && (
                        <div style={{ 
                          padding: '6px', 
                          backgroundColor: '#f6ffed', 
                          borderRadius: '4px',
                          fontStyle: 'italic',
                          fontSize: '11px'
                        }}>
                          {questionDifficulty.easiest.note}
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              )}
            </Row>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '20px', 
              color: '#999',
              backgroundColor: '#fafafa',
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              minHeight: '120px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <QuestionCircleOutlined style={{ fontSize: '28px', marginBottom: '8px', color: '#d9d9d9' }} />
              <Title level={5} style={{ color: '#999', marginBottom: '4px' }}>No Question Analysis Available</Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>Complete more responses to see detailed question analysis</Text>
            </div>
          )}
        </>
      ),
      style: panelStyle,
    },
    {
      key: '5',
      label: (
        <span>
          <TeamOutlined style={{ marginRight: '8px' }} />
          Campaign Targets
        </span>
      ),
      children: (
        <>
          {/* Search and Filters */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            <Col span={12}>
              <Search
                placeholder="Search by name, email..."
                allowClear
                onSearch={handleSearch}
                onChange={(e) => {
                  if (!e.target.value) {
                    handleSearch('');
                  }
                }}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={6}>
              <Select
                placeholder="Filter by status"
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={handleStatusFilter}
              >
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="in-progress">In Progress</Option>
                <Option value="completed">Completed</Option>
              </Select>
            </Col>
            <Col span={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {`Showing ${targets.length} of ${apiTargetTotal ?? '?'} targets`}
              </Text>
              <Tooltip title="Export to CSV">
                <Button
                  shape="circle"
                  icon={<DownloadOutlined />}
                  onClick={() => setExportModalOpen(true)}
                  style={{ borderColor: '#d9d9d9', color: '#595959' }}
                />
              </Tooltip>
            </Col>
          </Row>

          {/* ── Export modal ── */}
          <Modal
            open={exportModalOpen}
            onCancel={() => setExportModalOpen(false)}
            footer={null}
            width={500}
            styles={{ body: { padding: 0 } }}
            title={null}
            closable={false}
          >
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #1a3a6b 0%, #267a96 100%)', borderRadius: '8px 8px 0 0', padding: '20px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                    <DownloadOutlined style={{ marginRight: 8 }} />
                    Export to CSV
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
                    {apiTargetTotal ? `${apiTargetTotal} record${apiTargetTotal !== 1 ? 's' : ''}` : 'All records'}
                    {(statusFilter !== 'all' || searchText) ? ' · filtered view' : ' · all targets'}
                  </div>
                </div>
                <Button
                  type="text"
                  size="small"
                  onClick={() => setExportModalOpen(false)}
                  style={{ color: 'rgba(255,255,255,0.7)', marginTop: -4 }}
                >
                  ✕
                </Button>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '20px 24px' }}>
              {/* Selected count + quick actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Text style={{ fontSize: 13, color: '#1a1a1a' }}>
                  <strong>{exportFields.length}</strong>
                  <Text type="secondary" style={{ fontSize: 13 }}> of {ALL_EXPORT_FIELDS.length} fields selected</Text>
                </Text>
                <Space size={6}>
                  <Button size="small" type="link" style={{ padding: 0, fontSize: 12 }} onClick={() => setExportFields(ALL_EXPORT_FIELDS.map(f => f.key))}>All</Button>
                  <Text type="secondary" style={{ fontSize: 12 }}>·</Text>
                  <Button size="small" type="link" style={{ padding: 0, fontSize: 12 }} onClick={() => setExportFields(ALL_EXPORT_FIELDS.filter(f => f.default).map(f => f.key))}>Defaults</Button>
                  <Text type="secondary" style={{ fontSize: 12 }}>·</Text>
                  <Button size="small" type="link" style={{ padding: 0, fontSize: 12 }} onClick={() => setExportFields([])}>None</Button>
                </Space>
              </div>

              {/* Field groups */}
              {[
                {
                  label: 'Personal',
                  icon: <UserOutlined />,
                  keys: ['name', 'surname', 'email', 'phone'],
                },
                {
                  label: 'Assignment',
                  icon: <ApartmentOutlined />,
                  keys: ['businessUnit', 'department'],
                },
                {
                  label: 'Campaign Results',
                  icon: <TrophyOutlined />,
                  keys: ['status', 'score', 'startedAt', 'completedAt'],
                },
                {
                  label: 'Delivery',
                  icon: <SendOutlined />,
                  keys: ['deliveryChannel'],
                },
                {
                  label: 'Extras',
                  icon: <ClockCircleOutlined />,
                  keys: ['completionTime', 'device'],
                },
              ].map(group => {
                const groupFields = ALL_EXPORT_FIELDS.filter(f => group.keys.includes(f.key));
                const allGroupSelected = groupFields.every(f => exportFields.includes(f.key));
                const toggleGroup = () => {
                  if (allGroupSelected) {
                    setExportFields(prev => prev.filter(k => !group.keys.includes(k)));
                  } else {
                    setExportFields(prev => [...new Set([...prev, ...group.keys])]);
                  }
                };
                return (
                  <div key={group.label} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Space size={6}>
                        <span style={{ color: '#267a96', fontSize: 13 }}>{group.icon}</span>
                        <Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#444' }}>{group.label}</Text>
                      </Space>
                      <Button size="small" type="link" style={{ padding: 0, fontSize: 11, color: allGroupSelected ? '#cf1322' : '#267a96' }} onClick={toggleGroup}>
                        {allGroupSelected ? 'Deselect' : 'Select all'}
                      </Button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {groupFields.map(f => {
                        const selected = exportFields.includes(f.key);
                        return (
                          <div
                            key={f.key}
                            onClick={() => setExportFields(prev => selected ? prev.filter(k => k !== f.key) : [...prev, f.key])}
                            style={{
                              cursor: 'pointer',
                              padding: '5px 12px',
                              borderRadius: 20,
                              fontSize: 12,
                              fontWeight: selected ? 600 : 400,
                              background: selected ? '#e6f4f1' : '#f5f5f5',
                              color: selected ? '#0d7a5f' : '#666',
                              border: `1px solid ${selected ? '#a8d5c8' : '#e0e0e0'}`,
                              userSelect: 'none',
                              transition: 'all 0.15s',
                            }}
                          >
                            {selected && <span style={{ marginRight: 5, fontSize: 10 }}>✓</span>}
                            {f.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#fafafa', borderRadius: '0 0 8px 8px' }}>
              <Button onClick={() => setExportModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={exportLoading}
                disabled={exportFields.length === 0}
                onClick={handleExport}
                style={{ background: '#267a96', borderColor: '#267a96' }}
              >
                Export {apiTargetTotal ? `${apiTargetTotal} rows` : 'CSV'}
              </Button>
            </div>
          </Modal>

          <Table
            columns={columns}
            dataSource={targets}
            rowKey="_id"
            pagination={{
              current: currentPage,
              pageSize,
              total: apiTargetTotal ?? targets.length,
              showSizeChanger: true,
              pageSizeOptions: ['50', '100', '150', '200'],
              onChange: handleTablePageChange,
              onShowSizeChange: handleTablePageChange,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} targets`,
            }}
            loading={targetsLoading}
            scroll={{ x: 1200 }}
            locale={{
              emptyText: targetsLoading ? 'Loading targets...' : 'No targets found'
            }}
          />
        </>
      ),
      style: panelStyle,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title={`${campaign?.name || 'Campaign'} - Report`}
        breadcrumb={BREADCRUMB_PATTERNS.campaignReport(campaign?.name || 'Campaign Report')}
      />

      {/* AI Insights Button */}
      <div style={{ marginBottom: '16px' }}>
        <Button
          onClick={handleGetAIInsights}
          disabled={!stats.completed}
          style={{
            background: stats.completed ? 'linear-gradient(135deg, #2E86AB 0%, #1a5f7a 100%)' : '#f5f5f5',
            border: 'none',
            color: stats.completed ? '#fff' : '#999',
            fontWeight: '500'
          }}
        >
          <Space>
            <span style={{ fontSize: '14px' }}>✦</span>
            Quick AI Insights
          </Space>
        </Button>
        {!stats.completed && (
          <Text type="secondary" style={{ marginLeft: '12px', fontSize: '12px' }}>
            Requires completed responses
          </Text>
        )}
      </div>

      {/* Collapsible Report Sections */}
      <Collapse
        bordered={false}
        defaultActiveKey={['1', '2']}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
        style={{ background: token.colorBgContainer }}
        items={getCollapseItems(panelStyle)}
      />

      <MessagingModal
        open={messageModalOpen}
        mode={messageModalMode}
        target={messageModalTarget}
        requiresOverride={messageModalRequiresOverride}
        onClose={handleCloseMessagingModal}
        onSent={handleMessageSent}
      />

      <Modal
        open={answersModalVisible}
        onCancel={handleCloseAnswersModal}
        footer={null}
        width={900}
        title={(() => {
          if (!selectedTarget?.participantId) {
            return 'Participant Responses';
          }
          const fullName = `${selectedTarget.participantId.name || ''} ${selectedTarget.participantId.surname || ''}`.trim();
          const label = fullName || selectedTarget.participantId.email || 'Participant';
          return `Responses • ${label}`;
        })()}
      >
        {answersLoading ? (
          <div style={{ minHeight: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spin size="large" />
          </div>
        ) : participantAnswers ? (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="Participant">
                {participantAnswers.participant
                  ? `${participantAnswers.participant.name || ''} ${participantAnswers.participant.surname || ''}`.trim() || participantAnswers.participant.email
                  : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {participantAnswers.participant?.email || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Score">
                {participantAnswers.score !== undefined && participantAnswers.score !== null
                  ? `${participantAnswers.score}%`
                  : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Completed At">
                {participantAnswers.completedAt
                  ? new Date(participantAnswers.completedAt).toLocaleString()
                  : 'N/A'}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '4px' }}>
              {(participantAnswers.questions || []).map((question, index) => {
                const questionId = question?.id || question?._id || question?.questionId || `question-${index}`;
                const questionTitle = question?.question || question?.questionText || question?.title || `Question ${index + 1}`;
                const userAnswer = participantAnswers.userAnswers?.find(answer => {
                  if (!answer) return false;
                  if (answer.questionId && questionId) {
                    return String(answer.questionId) === String(questionId);
                  }
                  if (answer.questionText && questionTitle) {
                    return answer.questionText.trim() === questionTitle.trim();
                  }
                  return false;
                }) || participantAnswers.userAnswers?.[index];
                const selections = normalizeAnswerSelections(userAnswer?.answer);
                const selectionSet = new Set(
                  selections
                    .filter(val => val !== undefined && val !== null)
                    .map(val => (typeof val === 'object' ? JSON.stringify(val) : String(val)))
                );
                const hasOptions = Array.isArray(question?.answers) && question.answers.length > 0;
                const questionStatusColor = userAnswer ? (userAnswer.isCorrect ? 'success' : 'error') : 'default';
                const questionStatusText = userAnswer ? (userAnswer.isCorrect ? 'Correct' : 'Incorrect') : 'Not answered';

                return (
                  <Card
                    key={questionId || index}
                    size="small"
                    style={{ marginBottom: '12px', borderColor: '#f0f0f0' }}
                    bodyStyle={{ padding: '12px 16px' }}
                  >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                        <Text strong>{`${index + 1}. ${questionTitle}`}</Text>
                        <Tag color={questionStatusColor}>{questionStatusText}</Tag>
                      </div>

                      {hasOptions ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {question.answers.map((answerOption, answerIndex) => {
                            const optionIdentifiers = [
                              answerOption?.id,
                              answerOption?._id,
                              answerOption?.value,
                              answerOption?.key,
                              answerOption?.label,
                              answerIndex
                            ]
                              .filter(val => val !== undefined && val !== null)
                              .map(val => (typeof val === 'object' ? JSON.stringify(val) : String(val)));
                            const isSelected = optionIdentifiers.some(identifier => selectionSet.has(identifier));
                            const isCorrect = Boolean(answerOption?.isCorrect);

                            return (
                              <div
                                key={`${questionId}-${answerOption?.id || answerOption?._id || answerIndex}`}
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: 8,
                                  border: `1px solid ${isSelected ? token.colorPrimaryBorder : '#f0f0f0'}`,
                                  backgroundColor: isSelected ? 'rgba(82, 196, 26, 0.08)' : '#fff'
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                  <Text>
                                    {`${String.fromCharCode(65 + answerIndex)}. ${answerOption?.text || answerOption?.label || 'Answer option'}`}
                                  </Text>
                                  <Space size={4}>
                                    {isSelected && (
                                      <Tag color={isCorrect ? 'success' : 'error'}>
                                        {isCorrect ? 'Selected • Correct' : 'Selected'}
                                      </Tag>
                                    )}
                                    {!isSelected && isCorrect && (
                                      <Tag color="processing">Correct</Tag>
                                    )}
                                  </Space>
                                </div>
                              </div>
                            );
                          })}
                          {selectionSet.size === 0 && (
                            <Text type="secondary">No option selected</Text>
                          )}
                        </div>
                      ) : (
                        <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                            {formatAnswerValue(userAnswer?.answer)}
                          </pre>
                        </div>
                      )}
                    </Space>
                  </Card>
                );
              })}

              {(!participantAnswers.questions || participantAnswers.questions.length === 0) && (
                <Text type="secondary">No questionnaire data available for this participant.</Text>
              )}
            </div>
          </>
        ) : (
          <Text type="secondary">No responses available for this participant.</Text>
        )}
      </Modal>

      {/* Attempts History Modal */}
      <Modal
        open={attemptsModalVisible}
        onCancel={handleCloseAttemptsModal}
        footer={null}
        width={800}
        title={(() => {
          if (!attemptsModalTarget?.participantId) {
            return 'Attempt History';
          }
          const fullName = `${attemptsModalTarget.participantId.name || ''} ${attemptsModalTarget.participantId.surname || ''}`.trim();
          const label = fullName || attemptsModalTarget.participantId.email || 'Participant';
          return `Attempt History • ${label}`;
        })()}
      >
        {attemptsModalTarget?.attempts && attemptsModalTarget.attempts.length > 0 ? (
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {attemptsModalTarget.attempts.slice().reverse().map((attempt, attemptIndex) => {
              const attemptNumber = attemptsModalTarget.attempts.length - attemptIndex;
              const scoreColor = (attempt.score || 0) >= 70 ? 'success' : (attempt.score || 0) >= 50 ? 'warning' : 'error';
              
              return (
                <Card
                  key={attemptIndex}
                  size="small"
                  style={{ marginBottom: '12px' }}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Space>
                        <Text strong>Attempt {attemptNumber}</Text>
                        <Tag color={scoreColor}>{attempt.score || 0}%</Tag>
                      </Space>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {attempt.ts ? new Date(attempt.ts).toLocaleString() : 'N/A'}
                      </Text>
                    </div>
                  }
                  extra={
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Duration: {formatAttemptDuration(attempt.dur)}
                    </Text>
                  }
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {(attempt.ans || []).map((answer, ansIndex) => {
                      const isCorrect = answer.c;
                      return (
                        <Tooltip 
                          key={ansIndex} 
                          title={`Q${ansIndex + 1}: ${answer.a?.length ? answer.a.join(', ') : 'No answer'}`}
                        >
                          <Tag 
                            color={isCorrect ? 'success' : 'error'}
                            style={{ margin: 0, cursor: 'pointer' }}
                          >
                            Q{ansIndex + 1}: {isCorrect ? '✓' : '✗'}
                          </Tag>
                        </Tooltip>
                      );
                    })}
                  </div>
                  {attempt.ans && attempt.ans.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {attempt.ans.filter(a => a.c).length} correct out of {attempt.ans.length} questions
                      </Text>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        ) : (
          <Text type="secondary">No attempt history available.</Text>
        )}
      </Modal>

      {/* AI Insights Modal */}
      <Modal
        title={
          <Space>
            <span style={{ 
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              background: 'linear-gradient(135deg, #2E86AB 0%, #1a5f7a 100%)',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '14px'
            }}>✦</span>
            <span style={{ fontWeight: 600 }}>Campaign Insights</span>
          </Space>
        }
        open={aiInsightModalOpen}
        onCancel={handleCloseAIInsightModal}
        footer={[
          <Button key="close" onClick={handleCloseAIInsightModal}>
            Close
          </Button>
        ]}
        width={720}
      >
        {aiInsightLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: '#666' }}>
              {aiInsightStatus || 'Connecting...'}
            </div>
          </div>
        ) : aiInsight ? (
          <div>
            <div style={{ marginBottom: '16px' }}>
              {aiInsight.insight?.split('\n').filter(line => line.trim()).map((line, idx) => {
                const lineUpper = line.toUpperCase();
                const isOverview = lineUpper.includes('OVERVIEW');
                const isDelivery = lineUpper.includes('DELIVERY');
                const isEngagement = lineUpper.includes('ENGAGEMENT');
                const isParticipants = (lineUpper.includes('PARTICIPANTS') || lineUpper.includes('PARTICIPANT')) && !isEngagement;
                const isTeams = lineUpper.includes('TEAMS') || lineUpper.includes('TEAM');
                const isDepartments = (lineUpper.includes('DEPARTMENTS') || lineUpper.includes('DEPARTMENT')) && !isTeams;
                const isQuestions = lineUpper.includes('QUESTIONS') || lineUpper.includes('QUESTION');
                const isDropoffs = lineUpper.includes('DROPOFF');
                const isRetake = lineUpper.includes('RETAKE');
                const isInsight = lineUpper.includes('INSIGHT') && !isOverview;
                const isAction = lineUpper.includes('ACTION');
                
                let bgColor = 'rgba(46,134,171,0.06)';
                let leftBorderColor = '#2E86AB';
                
                if (isOverview) { 
                  bgColor = 'rgba(46,134,171,0.08)'; 
                  leftBorderColor = '#2E86AB';
                }
                if (isDelivery) { 
                  bgColor = 'rgba(114,46,209,0.08)'; 
                  leftBorderColor = '#722ed1';
                }
                if (isEngagement) { 
                  bgColor = 'rgba(47,84,235,0.08)'; 
                  leftBorderColor = '#2f54eb';
                }
                if (isDropoffs) { 
                  bgColor = 'rgba(235,47,150,0.08)'; 
                  leftBorderColor = '#eb2f96';
                }
                if (isParticipants) { 
                  bgColor = 'rgba(235,47,150,0.08)'; 
                  leftBorderColor = '#eb2f96';
                }
                if (isTeams || isDepartments) { 
                  bgColor = 'rgba(19,194,194,0.08)'; 
                  leftBorderColor = '#13c2c2';
                }
                if (isQuestions) { 
                  bgColor = 'rgba(82,196,26,0.08)'; 
                  leftBorderColor = '#52c41a';
                }
                if (isRetake) { 
                  bgColor = 'rgba(250,173,20,0.08)'; 
                  leftBorderColor = '#faad14';
                }
                if (isInsight) { 
                  bgColor = 'rgba(82,196,26,0.08)'; 
                  leftBorderColor = '#52c41a';
                }
                if (isAction) { 
                  bgColor = 'rgba(250,140,22,0.08)'; 
                  leftBorderColor = '#fa8c16';
                }
                
                return (
                  <div 
                    key={idx}
                    style={{ 
                      padding: '12px 14px', 
                      background: bgColor,
                      borderLeft: `4px solid ${leftBorderColor}`,
                      borderRadius: '0 6px 6px 0',
                      marginBottom: '10px',
                      lineHeight: '1.6',
                      fontSize: '13px'
                    }}
                  >
                    {line}
                  </div>
                );
              })}
            </div>
            {aiInsight.stats && (
              <div style={{ 
                padding: '12px', 
                background: '#fafafa', 
                borderRadius: '8px',
                fontSize: '12px'
              }}>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <div><Text type="secondary">Participants:</Text> {aiInsight.stats.total}</div>
                  <div><Text type="secondary">Completed:</Text> {aiInsight.stats.completed}</div>
                  {aiInsight.stats.inProgress > 0 && (
                    <div><Text type="secondary">In Progress:</Text> {aiInsight.stats.inProgress}</div>
                  )}
                  <div><Text type="secondary">Rate:</Text> {aiInsight.stats.completionRate}%</div>
                  <div><Text type="secondary">Avg Score:</Text> {aiInsight.stats.averageScore}%</div>
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {aiInsight.stats.smsDeliveryRate > 0 && (
                    <div><Text type="secondary">SMS Delivery:</Text> {aiInsight.stats.smsDeliveryRate}%</div>
                  )}
                  {aiInsight.stats.waDeliveryRate > 0 && (
                    <div><Text type="secondary">WhatsApp:</Text> {aiInsight.stats.waDeliveryRate}%</div>
                  )}
                  {aiInsight.stats.daysActive > 0 && (
                    <div><Text type="secondary">Days Active:</Text> {aiInsight.stats.daysActive}</div>
                  )}
                  {aiInsight.stats.daysLeft !== null && aiInsight.stats.daysLeft !== undefined && (
                    <div><Text type="secondary">Days Left:</Text> {aiInsight.stats.daysLeft}</div>
                  )}
                </div>
              </div>
            )}

            {/* Follow-up conversation */}
            {aiFollowUpMessages.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <Divider style={{ margin: '0 0 12px 0' }}>
                  <span style={{ fontSize: '12px', color: '#999' }}>Follow-up Questions</span>
                </Divider>
                {aiFollowUpMessages.map((msg, idx) => (
                  <div key={idx} style={{
                    padding: '10px 14px',
                    background: msg.role === 'user' ? 'rgba(46,134,171,0.06)' : '#fafafa',
                    borderLeft: msg.role === 'user' ? '4px solid #2E86AB' : '4px solid #d9d9d9',
                    borderRadius: '0 6px 6px 0',
                    marginBottom: '8px',
                    lineHeight: '1.6',
                    fontSize: '13px'
                  }}>
                    {msg.role === 'user' ? (
                      <div>
                        <span style={{ fontSize: '11px', color: '#2E86AB', fontWeight: 600 }}>You asked:</span>
                        <div style={{ marginTop: '2px' }}>{msg.content}</div>
                      </div>
                    ) : (
                      <div>{msg.content}</div>
                    )}
                  </div>
                ))}
                {aiFollowUpLoading && (
                  <div style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <Spin size="small" />
                    <span style={{ marginLeft: '8px', color: '#999', fontSize: '12px' }}>Thinking...</span>
                  </div>
                )}
                <div ref={aiChatEndRef} />
              </div>
            )}

            {/* Follow-up input */}
            {aiSessionId && (
              <div style={{
                marginTop: '12px',
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-start'
              }}>
                <Input.TextArea
                  value={aiFollowUpInput}
                  onChange={(e) => setAiFollowUpInput(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleAskAIFollowUp();
                    }
                  }}
                  placeholder="Ask a follow-up question about this campaign..."
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  disabled={aiFollowUpLoading}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleAskAIFollowUp}
                  loading={aiFollowUpLoading}
                  disabled={!aiFollowUpInput.trim()}
                  style={{
                    background: 'linear-gradient(135deg, #2E86AB 0%, #1a5f7a 100%)',
                    border: 'none',
                    height: '36px'
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            No insights available
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CampaignReport;